import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Target, Zap, Activity, Loader, AlertCircle, Wallet, Plus, Settings, Play, Pause, Trash2, Edit2, ToggleLeft, ToggleRight, History, TrendingUp, Info, Copy, Check, Search, ExternalLink, Globe, MessageCircle, Youtube, Pill, X } from 'lucide-react';
import { globalWidgetTextStyles, widgetClassName } from '../../styles/globalWidgetStyles';
import { wsService } from '../../services/websocketService';
import { useWalletUser } from '../../hooks/useWalletUser';
import { getUserTradingWallets, restoreTradingWalletKeypair } from '../../services/tradingWalletService';
import { getBalance } from '../../services/shyftService';
import { getTokenBalance } from '../../services/solanatrackerService';
import { tradeEngine } from '../../services/tradeEngine';
import { 
  sniperRulesService, 
  SniperRule, 
  SniperCondition,
  SNIPER_FIELD_LABELS
} from '../../services/sniperRulesService';
import { sniperPositionsService } from '../../services/sniperPositionsService';
import SniperRuleModal from './SniperRuleModal';
import { adminSupabase } from '../../services/supabaseService';
import GlobalDropdown from '../../components/GlobalDropdown';
import { userService } from '../../services/userService';
import { createAdvancedChartsWidget } from '../AdvancedCharts/AdvancedCharts';
import { createTokenInfoWidget } from '../TokenInfo/TokenInfo';
import { Token } from '../../types/supabase';

// Types for sniper rules (local interfaces that match the service)
interface LocalSniperRule {
  id: string;
  name: string;
  isActive: boolean;
  conditions: LocalSniperCondition[];
  createdAt: string;
  // Configuration from database
  solAmountPerSnipe?: number;
  maxTokensToSnipe?: number;
  slippagePercent?: number;
  tradingWalletAddresses?: string[];
  totalSolBudget?: number;
  configurationMetadata?: any;
  enableSniperMirroring?: boolean; // New option for wallet mirroring
  // Auto-sell configuration
  enableAutoSell?: boolean;
  autoSellProfitPercent?: number;
  autoSellLossPercent?: number;
  autoSellPositionPercent?: number; // % of position to sell (default 100%)
}

interface LocalSniperCondition {
  field: 'marketCap' | 'liquidity' | 'holders' | 'buys' | 'sells' | 'curvePercentage' | 'tokensBought' | 'txVolume' | 'name' | 'symbol';
  operator: '>' | '<' | '>=' | '<=' | '=' | 'increase%' | 'decrease%';
  value: number;
}

interface TradingWallet {
  id: string;
  name: string;
  public_key: string;
  created_at: string;
  owner_wallet_address: string;
  is_active: boolean;
  last_used_at?: string;
  balance?: number;
}

interface SelectedWallet extends TradingWallet {
  solBalance: number;
  isSelected: boolean;
}

// Define the props for the SniperBot component
export interface SniperBotProps {
  onClose?: () => void;
  isWidget?: boolean;
  onAddWidget?: (widget: any) => void; // For adding widgets to canvas
}

const SniperBot: React.FC<SniperBotProps> = ({
  onClose,
  isWidget = false,
  onAddWidget
}) => {
  const walletUser = useWalletUser();
  
  // Custom notification functions to replace react-hot-toast
  const showNotification = (message: string, type: 'success' | 'error' | 'loading', id?: string) => {
    const toastId = id || `sniper-${type}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    
    window.dispatchEvent(new CustomEvent('ekko:show_toast', {
      detail: {
        message,
        type,
        id: toastId
      }
    }));
    
    return toastId;
  };

  const hideNotification = (id: string, type?: 'success' | 'error' | 'loading') => {
    window.dispatchEvent(new CustomEvent('ekko:hide_toast', {
      detail: {
        id,
        type
      }
    }));
  };

  const updateNotification = (id: string, message: string, type: 'success' | 'error' | 'loading') => {
    window.dispatchEvent(new CustomEvent('ekko:update_toast', {
      detail: {
        id,
        message,
        type
      }
    }));
  };

  // Helper function to get effective wallet address (use direct connection)
  const getEffectiveWalletAddress = (): string | null => {
    // Use direct wallet connection since it's always reliable
    if (typeof window !== 'undefined' && window.solana?.publicKey) {
      return window.solana.publicKey.toString();
    }
    
    // Fallback to hook only if direct connection fails
    if (walletUser.walletAddress && walletUser.walletAddress.trim() !== '') {
      console.warn('⚠️ Using hook as fallback (direct wallet unavailable):', walletUser.walletAddress);
      return walletUser.walletAddress;
    }
    
    return null;
  };
  
  // State management
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'connecting' | 'disconnected' | 'error'>('disconnected');
  const [isLoading, setIsLoading] = useState(false);
  const subscribedTokensRef = useRef<Set<string>>(new Set());
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const tokenSubscriptionsRef = useRef<Map<string, (() => void)[]>>(new Map());
  const tokenMetadataRef = useRef<Map<string, { name: string; symbol: string }>>(new Map());
  const masterTokenDataRef = useRef<Map<string, any>>(new Map());

  // Trading wallet management
  const [tradingWallets, setTradingWallets] = useState<TradingWallet[]>([]);
  const [selectedWallets, setSelectedWallets] = useState<Set<string>>(new Set());
  const [walletBalances, setWalletBalances] = useState<Map<string, number>>(new Map());
  const [isLoadingWallets, setIsLoadingWallets] = useState(false);

  // Sniper configuration
  const [solAmountPerSnipe, setSolAmountPerSnipe] = useState<string>('0.1');
  const [maxTokensToSnipe, setMaxTokensToSnipe] = useState<string>('5');
  const [slippagePercent, setSlippagePercent] = useState<string>('35');
  const [isSnipingActive, setIsSnipingActive] = useState(false);
  const isSnipingActiveRef = useRef<boolean>(false);

  // Auto-sell configuration
  const [enableAutoSell, setEnableAutoSell] = useState<boolean>(false);
  const [autoSellProfitPercent, setAutoSellProfitPercent] = useState<string>('50');
  const [autoSellLossPercent, setAutoSellLossPercent] = useState<string>('25');
  const [autoSellPositionPercent, setAutoSellPositionPercent] = useState<string>('100');

  // Rules management
  const [sniperRules, setSniperRules] = useState<LocalSniperRule[]>([]);
  const sniperRulesRef = useRef<LocalSniperRule[]>([]);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState<SniperRule | null>(null);
  const [isLoadingRules, setIsLoadingRules] = useState(false);

  // Current view state
  const [activeTab, setActiveTab] = useState<'monitor' | 'transactions'>('monitor');
  
  // Sniper bot creation flow
  const [isCreatingBot, setIsCreatingBot] = useState(false);
  const [creationStep, setCreationStep] = useState<'rules' | 'config'>('rules');
  const [newBotName, setNewBotName] = useState('');
  const [newBotRules, setNewBotRules] = useState<LocalSniperCondition[]>([]);
  const [editingBotId, setEditingBotId] = useState<string | null>(null); // Track if we're editing
  const [sniperStrategy, setSniperStrategy] = useState<'diversified' | 'mirrored' | 'individual'>('diversified'); // New strategy enum

  // Deletion confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<{ id: string; name: string; isActive: boolean } | null>(null);

  // Performance optimization refs
  const maxTrackedTokens = 50; // Increased limit for better tracking
  const cleanupThreshold = 47; // Start cleanup before hitting the limit
  const tokensToRemoveCount = 10; // Remove first 10 tokens for efficiency

  // Total tokens tracked over time (frontend counter) - USE REF to prevent re-renders
  const totalTokensTrackedRef = useRef<number>(0);
  const [displayTotalTokensTracked, setDisplayTotalTokensTracked] = useState(0);

  // Snipe tracking per rule (ruleId -> count)
  const ruleSnipeCountRef = useRef<Map<string, number>>(new Map());
  
  // Track recently sniped tokens to prevent duplicates (tokenAddress -> timestamp)
  const recentlySnipedTokensRef = useRef<Map<string, number>>(new Map());
  const DUPLICATE_PREVENTION_TIME = 60000; // 1 minute
  
  // Track permanently sniped tokens per rule for individual mode (ruleId -> Set<tokenAddress>)
  const rulePermanentSnipesRef = useRef<Map<string, Set<string>>>(new Map());

  // Auto-sell transactions state
  const [autoSellTransactions, setAutoSellTransactions] = useState<any[]>([]);
  
  // Track per-wallet snipe counts for individual mode (ruleId -> walletAddress -> count)
  const ruleWalletSnipeCountRef = useRef<Map<string, Map<string, number>>>(new Map());
  
  // Track wallet rotation for round-robin distribution (ruleId -> walletIndex)
  const walletRotationRef = useRef<Map<string, number>>(new Map());
  
  // Track executing trades per rule to prevent race conditions (ruleId -> Promise)
  const ruleExecutionLockRef = useRef<Map<string, Promise<void>>>(new Map());

  // Track baseline values for percentage calculations (ruleId -> tokenAddress -> fieldName -> baselineValue)
  const ruleBaselinesRef = useRef<Map<string, Map<string, Map<string, number>>>>(new Map());

  // Position monitoring for auto-sell
  const positionMonitoringCleanupRef = useRef<(() => void) | null>(null);

  // Trade history management
  const [tradeHistory, setTradeHistory] = useState<any[]>([]);
  const [isLoadingTradeHistory, setIsLoadingTradeHistory] = useState(false);
  const realtimeSubscriptionRef = useRef<any>(null);

  // Rule statistics (ruleId -> {triggers: number, success: number, failed: number})
  const [ruleStatistics, setRuleStatistics] = useState<Map<string, {triggers: number, success: number, failed: number}>>(new Map());

  // Copy to clipboard state
  const [copiedTokens, setCopiedTokens] = useState<Set<string>>(new Set());

  // Token data cache for transactions - USE REF to prevent re-renders
  const tokenDataCacheRef = useRef<Map<string, any>>(new Map());
  const [, forceUpdate] = useState(0); // Only for manual re-renders when needed
  
  // Loading token data state - USE REF to prevent re-renders
  const loadingTokenDataRef = useRef<Set<string>>(new Set());

  // Fetch token data from Solana Tracker API
  const fetchTokenData = async (tokenAddress: string) => {
    // Check if already cached
    if (tokenDataCacheRef.current.has(tokenAddress)) {
      return tokenDataCacheRef.current.get(tokenAddress);
    }

    // Check if already loading
    if (loadingTokenDataRef.current.has(tokenAddress)) {
      return null;
    }

    try {
      loadingTokenDataRef.current.add(tokenAddress);

      const apiKey = import.meta.env.VITE_SOLANA_TRACKER_API_KEY;
      if (!apiKey) {
        console.error('Solana Tracker API key not found');
        return null;
      }

      const response = await fetch(`https://data.solanatracker.io/tokens/${tokenAddress}`, {
        headers: {
          'x-api-key': apiKey,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Cache the data
      tokenDataCacheRef.current.set(tokenAddress, data);
      
      console.log(`✅ Fetched token data for ${tokenAddress}:`, data.token?.symbol || 'Unknown');
      return data;
    } catch (error) {
      console.error(`Error fetching token data for ${tokenAddress}:`, error);
      return null;
    } finally {
      loadingTokenDataRef.current.delete(tokenAddress);
    }
  };

  // Poll token data every 3 seconds for tracked tokens
  const pollTokenData = async (tokenAddress: string) => {
    try {
      const apiKey = import.meta.env.VITE_SOLANA_TRACKER_API_KEY;
      if (!apiKey) {
        console.error('Solana Tracker API key not found');
        return;
      }

      const response = await fetch(`https://data.solanatracker.io/tokens/${tokenAddress}`, {
        headers: {
          'x-api-key': apiKey,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Update cache with fresh data
      tokenDataCacheRef.current.set(tokenAddress, data);
      
      // Extract relevant data from the response
      const currentData = masterTokenDataRef.current.get(tokenAddress) || {};
      const primaryPool = data.pools && data.pools.length > 0 ? data.pools[0] : null;
      const tokenInfo = tokenMetadataRef.current.get(tokenAddress);
      
      // Track what changed for logging
      const changes = [];
      const newHolders = data.holders || currentData.holders || 0;
      const newBuys = primaryPool?.txns?.buys ? 
        Math.max(primaryPool.txns.buys, currentData.buys || 0) : 
        (currentData.buys || 0);
      const newSells = primaryPool?.txns?.sells ? 
        Math.max(primaryPool.txns.sells, currentData.sells || 0) : 
        (currentData.sells || 0);
      
      // Log changes
      if (newHolders !== (currentData.holders || 0)) {
        changes.push(`holders: ${currentData.holders || 0} → ${newHolders}`);
      }
      if (newBuys !== (currentData.buys || 0)) {
        changes.push(`buys: ${currentData.buys || 0} → ${newBuys}`);
      }
      if (newSells !== (currentData.sells || 0)) {
        changes.push(`sells: ${currentData.sells || 0} → ${newSells}`);
      }
      
      const updatedData = {
        // Always update holders (main goal)
        holders: newHolders,
        
        // Update buys/sells only if new values are higher
        buys: newBuys,
        sells: newSells,
        
        // Update other useful data
        marketCapUsd: primaryPool?.marketCap?.usd || currentData.marketCapUsd,
        liquidityUsd: primaryPool?.liquidity?.usd || currentData.liquidityUsd,
        priceUsd: primaryPool?.price?.usd || currentData.priceUsd,
        
        // Update transaction totals
        totalTxns: primaryPool?.txns?.total || currentData.totalTxns,
        txVolume: primaryPool?.txns?.volume || currentData.txVolume,
        
        // Add poll timestamp
        lastPolled: new Date().toLocaleTimeString(),
        pollCount: (currentData.pollCount || 0) + 1
      };
      
      // Update the token table with new data
      updateTokenTable(tokenAddress, updatedData, 'POLL UPDATE');
      
      // Log poll results
      if (changes.length > 0) {
        console.log(`🔄 POLL UPDATE: ${tokenInfo?.symbol || tokenAddress.slice(0, 8)} - ${changes.join(', ')}`);
      } else {
        console.log(`📊 POLL: ${tokenInfo?.symbol || tokenAddress.slice(0, 8)} - no changes (poll #${updatedData.pollCount})`);
      }
    } catch (error) {
      console.error(`Error polling token data for ${tokenAddress}:`, error);
    }
  };

  // Render social links for token (matching TokenCard styling exactly)
  const renderTokenSocials = (tokenData: any, tokenAddress: string, tokenSymbol: string) => {
    if (!tokenData?.token) return null;

    const { token } = tokenData;
    const socials = token.strictSocials || {};
    
    return (
      <div className="mb-2">
        {/* Social Links - Exact TokenCard styling */}
        <div className="flex gap-1">
          {/* X.com Search */}
          <a
            href={`https://x.com/search?q=${tokenSymbol}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-black rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80"
            title="Search on Twitter"
          >
            <Search size={10} className="text-white" />
          </a>

          {/* Pump.fun pill */}
          <a
            href={`https://pump.fun/coin/${tokenAddress}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-black rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80"
            title="View on Pump.fun"
          >
            <Pill size={10} className="text-white" />
          </a>

          {/* Twitter/X Official */}
          {socials.twitter && (
            <a
              href={socials.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80"
              title="Official Twitter"
            >
              <img 
                src="https://cdn.cms-twdigitalassets.com/content/dam/about-twitter/x/brand-toolkit/logo-black.png.twimg.2560.png" 
                alt="X" 
                className="w-2.5 h-2.5 invert"
              />
            </a>
          )}

          {/* Telegram */}
          {socials.telegram && (
            <a
              href={socials.telegram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white bg-[#0088cc] rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80"
              title="Official Telegram"
            >
              <MessageCircle size={10} />
            </a>
          )}

          {/* YouTube */}
          {(token.youtube || socials.youtube) && (
            <a
              href={token.youtube || socials.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white bg-[#FF0000] rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80"
              title="Official YouTube"
            >
              <Youtube size={10} />
            </a>
          )}

          {/* Website */}
          {socials.website && (
            <a
              href={socials.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white bg-gray-600 rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80"
              title="Official Website"
            >
              <Globe size={10} />
            </a>
          )}
        </div>
      </div>
    );
  };

  // Memoized Timer component for transactions to prevent re-renders
  const TransactionTimer = React.memo<{ createdAt: string }>(({ createdAt }) => {
    const [elapsed, setElapsed] = useState('');

    useEffect(() => {
      const updateElapsed = () => {
        const now = new Date().getTime();
        const created = new Date(createdAt).getTime();
        const diff = now - created;

        if (diff < 0) {
          setElapsed('now');
          return;
        }

        const seconds = Math.floor(diff / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) {
          setElapsed(`${days}d`);
        } else if (hours > 0) {
          setElapsed(`${hours}h`);
        } else if (minutes > 0) {
          setElapsed(`${minutes}m`);
        } else {
          setElapsed(`${seconds}s`);
        }
      };

      updateElapsed();
      const interval = setInterval(updateElapsed, 1000);

      return () => clearInterval(interval);
    }, [createdAt]);

    return (
      <div className="text-[#24c35b] text-xs font-bold text-center">
        {elapsed}
      </div>
    );
  });

  // Memoized Token info component for transactions to prevent re-renders
  const TokenInfoDisplay = React.memo<{ tokenAddress: string; tokenSymbol: string }>(({ tokenAddress, tokenSymbol }) => {
    const [tokenData, setTokenData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
      const loadTokenData = async () => {
        if (tokenDataCacheRef.current.has(tokenAddress)) {
          setTokenData(tokenDataCacheRef.current.get(tokenAddress));
          return;
        }

        setIsLoading(true);
        const data = await fetchTokenData(tokenAddress);
        if (data) {
          setTokenData(data);
        }
        setIsLoading(false);
      };

      loadTokenData();
    }, [tokenAddress]);

    if (isLoading) {
      return (
        <div className="flex items-center gap-1 mb-2">
          <Loader size={10} className="text-gray-400 animate-spin" />
          <span className="text-gray-400 text-[10px]">Loading token info...</span>
        </div>
      );
    }

    if (!tokenData) {
      return null;
    }

    return renderTokenSocials(tokenData, tokenAddress, tokenSymbol);
  });

  // Functions to open widgets with specific tokens
  const openAdvancedChartsWidget = (tokenAddress: string, tokenSymbol: string, tokenName?: string) => {
    // Create a properly formed token object with default values that won't break the chart
    const tokenData: Token = {
      // Essential identification fields
      mint: tokenAddress,
      symbol: tokenSymbol,
      name: tokenName || tokenSymbol,
      id: tokenAddress,

      // Default values for required fields (will be updated by the chart widget)
      signature: '',
      trader_public_key: '',
      tx_type: 'buy',
      sol_amount: 0,
      token_amount: 0,
      timestamp: new Date().toISOString(),
      price_usd: 0,
      market_cap_usd: 0,
      liquidity_usd: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),

      // Additional fields that might be needed
      initial_buy: null,
      market_cap_sol: null,
      bonding_curve_key: null,
      v_sol_in_bonding_curve: null,
      v_tokens_in_bonding_curve: null,
      uri: null,
      metadata: null,
      description: null,
      image_uri: null,
      image_url: null,
      twitter: null,
      telegram: null,
      website: null,
      discord: null,
      attributes: null,
      dev_projects: null,
      is_rugged: null,
      rug_date: null,
      rug_type: null,
      buys: null,
      sells: null,
      holders: null,
      volume: null,
      liquidity: null,
      token_price: null,
      snipers: null,
      insiders: null,
      top_10_holders: null,
      bonding_curve_progress: null,
      sequence_number: null,
      volume_usd: null,
      tx_volume: null,
      buys_count: null,
      sells_count: null,
      holders_count: null,
      position: null,
      market_type: null,
      market: null,
      youtube: null,
      is_raydium: undefined,
      is_migrated: undefined,
      first_seen_in_trending: null,
      program: null,
      transaction_time: null,
      transaction_type: null,
      transaction_amount: null,
      transaction_price_usd: null,
      sol_volume: null,
      volume_5m: null,
      volume_15m: null,
      volume_30m: null,
      volume_1h: null,
      volume_6h: null,
      volume_12h: null,
      volume_24h: null,
      jupiter: null,
      risk: undefined,
      events: undefined,
      pools: undefined,
      dev_wallet: null,
      dev_buy_tokens: null,
      dev_buy_time: null,
      dev_sell_time: null
    };

    // FIRST: Dispatch token selection events to update existing chart widgets (same as TokenCard)
    // 1. Dispatch the token_selected event that chart widgets listen for
    const event = new CustomEvent('ekko:token_selected', { 
      detail: { token: tokenData, force_refresh: true }
    });
    window.dispatchEvent(event);
    
    // 2. Dispatch canvas_update_widgets event specifically for canvas
    const canvasUpdateEvent = new CustomEvent('ekko:canvas_update_widgets', { 
      detail: { token: tokenData, timestamp: Date.now() }
    });
    window.dispatchEvent(canvasUpdateEvent);

    // SECOND: Also create a new Advanced Charts widget if onAddWidget is available
    if (onAddWidget) {
    const widget = createAdvancedChartsWidget(tokenData);
    onAddWidget({
      ...widget,
      position: { x: 100, y: 100 } // Default position
    });
    
    console.log(`📈 Opened Advanced Charts widget for ${tokenSymbol} (${tokenAddress}) - chart will auto-load token data`);
    } else {
      console.log(`📈 Updated existing chart widgets with ${tokenSymbol} (${tokenAddress}) data`);
    }
  };

  const openTokenInfoWidget = (tokenAddress: string, tokenSymbol: string, tokenName?: string) => {
    // Create a properly formed token object for the token info widget
    const tokenData: Token = {
      // Essential identification fields
      mint: tokenAddress,
      symbol: tokenSymbol,
      name: tokenName || tokenSymbol,
      id: tokenAddress,

      // Default values for required fields
      signature: '',
      trader_public_key: '',
      tx_type: 'buy',
      sol_amount: 0,
      token_amount: 0,
      timestamp: new Date().toISOString(),
      price_usd: 0,
      market_cap_usd: 0,
      liquidity_usd: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),

      // Additional fields
      initial_buy: null,
      market_cap_sol: null,
      bonding_curve_key: null,
      v_sol_in_bonding_curve: null,
      v_tokens_in_bonding_curve: null,
      uri: null,
      metadata: null,
      description: null,
      image_uri: null,
      image_url: null,
      twitter: null,
      telegram: null,
      website: null,
      discord: null,
      attributes: null,
      dev_projects: null,
      is_rugged: null,
      rug_date: null,
      rug_type: null,
      buys: null,
      sells: null,
      holders: null,
      volume: null,
      liquidity: null,
      token_price: null,
      snipers: null,
      insiders: null,
      top_10_holders: null,
      bonding_curve_progress: null,
      sequence_number: null,
      volume_usd: null,
      tx_volume: null,
      buys_count: null,
      sells_count: null,
      holders_count: null,
      position: null,
      market_type: null,
      market: null,
      youtube: null,
      is_raydium: undefined,
      is_migrated: undefined,
      first_seen_in_trending: null,
      program: null,
      transaction_time: null,
      transaction_type: null,
      transaction_amount: null,
      transaction_price_usd: null,
      sol_volume: null,
      volume_5m: null,
      volume_15m: null,
      volume_30m: null,
      volume_1h: null,
      volume_6h: null,
      volume_12h: null,
      volume_24h: null,
      jupiter: null,
      risk: undefined,
      events: undefined,
      pools: undefined,
      dev_wallet: null,
      dev_buy_tokens: null,
      dev_buy_time: null,
      dev_sell_time: null
    };

    // FIRST: Dispatch token selection events to update existing widgets (same as TokenCard)
    // 1. Dispatch the token_selected event that other widgets listen for
    const event = new CustomEvent('ekko:token_selected', { 
      detail: { token: tokenData, force_refresh: true }
    });
    window.dispatchEvent(event);
    
    // 2. Dispatch canvas_update_widgets event specifically for canvas
    const canvasUpdateEvent = new CustomEvent('ekko:canvas_update_widgets', { 
      detail: { token: tokenData, timestamp: Date.now() }
    });
    window.dispatchEvent(canvasUpdateEvent);

    // SECOND: Also create a new Token Info widget if onAddWidget is available
    if (onAddWidget) {
    const widget = createTokenInfoWidget({ selectedToken: tokenData });
    onAddWidget({
      ...widget,
      position: { x: 200, y: 200 } // Default position, offset from charts
    });
    
    console.log(`ℹ️ Opened Token Info widget for ${tokenSymbol} (${tokenAddress}) - widget will load token data`);
    } else {
      console.log(`ℹ️ Updated existing widgets with ${tokenSymbol} (${tokenAddress}) data`);
    }
  };

  // Helper function to format rule conditions for display
  const formatCondition = (condition: LocalSniperCondition): string => {
    const fieldLabels: Record<string, string> = {
      marketCap: 'Market Cap',
      liquidity: 'Liquidity', 
      holders: 'Holders',
      buys: 'Buys',
      sells: 'Sells',
      curvePercentage: 'Curve %',
      tokensBought: 'Tokens Bought',
      txVolume: 'TX Volume'
    };

    const operatorLabels: Record<string, string> = {
      '>': '>',
      '<': '<', 
      '>=': '≥',
      '<=': '≤',
      '=': '=',
      'increase%': '↑',
      'decrease%': '↓'
    };

    const fieldName = fieldLabels[condition.field] || condition.field;
    const operator = operatorLabels[condition.operator] || condition.operator;
    const value = condition.value.toLocaleString();

    return `${fieldName} ${operator} ${value}`;
  };

  // Function to update and display the master token table
  const updateTokenTable = (tokenAddress: string, updateData: any, updateType: string) => {
    const currentData = masterTokenDataRef.current.get(tokenAddress) || {};
    const tokenInfo = tokenMetadataRef.current.get(tokenAddress) || { name: '', symbol: '' };
    
    // Merge the update data
    const updatedData = { ...currentData, ...updateData };
    
    // Store updated data
    masterTokenDataRef.current.set(tokenAddress, updatedData);
    
    // Cleanup old tokens using sliding window approach
    if (masterTokenDataRef.current.size >= cleanupThreshold) {
      const entries = Array.from(masterTokenDataRef.current.entries());
      const tokensToRemove = entries.slice(0, tokensToRemoveCount); // Remove first 10 tokens
      
      tokensToRemove.forEach(([address]) => {
        masterTokenDataRef.current.delete(address);
        tokenMetadataRef.current.delete(address);
        
        // Clean up WebSocket subscriptions for removed tokens
        const subscriptions = tokenSubscriptionsRef.current.get(address);
        if (subscriptions) {
          subscriptions.forEach(unsub => unsub());
          // Don't leave rooms - they might be used by token feeds
          // wsService.leaveRoom(`transaction:${address}`);
          // wsService.leaveRoom(`holders:${address}`);
          // wsService.leaveRoom(`token:${address}`);
          tokenSubscriptionsRef.current.delete(address);
        }
      });
      
      // Update subscribed tokens set
      subscribedTokensRef.current = new Set(Array.from(masterTokenDataRef.current.keys()));
    }
    
    // CRITICAL: Check if token meets sniper criteria - simplified
    const isSystemActive = isSnipingActiveRef.current;
    const currentRules = sniperRulesRef.current;
    
    // Only check if sniping is active and we have active rules
    if (isSystemActive && currentRules.length > 0) {
      const activeRules = currentRules.filter(rule => rule.isActive);
      if (activeRules.length > 0) {
        checkSniperRules(tokenAddress, updatedData);
      }
    }
  };

  // Helper function to map UI field names to actual token data field names
  const mapFieldToDataField = (field: LocalSniperCondition['field']): string => {
    const fieldMapping: Record<LocalSniperCondition['field'], string> = {
      'marketCap': 'marketCapUsd',
      'liquidity': 'liquidityUsd', 
      'holders': 'holders',
      'buys': 'buys',
      'sells': 'sells',
      'curvePercentage': 'curvePercentage',
      'tokensBought': 'tokensBought',
      'txVolume': 'lastTxVolume',
      'name': 'name',
      'symbol': 'symbol'
    };
    
    return fieldMapping[field] || field;
  };

  // Function to set baseline values for percentage calculations
  const setRuleBaseline = (ruleId: string, tokenAddress: string, fieldName: string, value: number) => {
    if (!ruleBaselinesRef.current.has(ruleId)) {
      ruleBaselinesRef.current.set(ruleId, new Map());
    }
    
    const ruleBaselines = ruleBaselinesRef.current.get(ruleId)!;
    if (!ruleBaselines.has(tokenAddress)) {
      ruleBaselines.set(tokenAddress, new Map());
    }
    
    const tokenBaselines = ruleBaselines.get(tokenAddress)!;
    if (!tokenBaselines.has(fieldName)) {
      tokenBaselines.set(fieldName, value);
      console.log(`📊 BASELINE SET: Rule "${ruleId}" - ${fieldName} baseline for ${tokenAddress.slice(0, 8)}... = ${value}`);
    }
  };

  // Function to get baseline value for percentage calculations
  const getRuleBaseline = (ruleId: string, tokenAddress: string, fieldName: string): number | null => {
    const ruleBaselines = ruleBaselinesRef.current.get(ruleId);
    if (!ruleBaselines) return null;
    
    const tokenBaselines = ruleBaselines.get(tokenAddress);
    if (!tokenBaselines) return null;
    
    return tokenBaselines.get(fieldName) || null;
  };

  // Function to calculate percentage change from baseline
  const calculatePercentageChange = (currentValue: number, baselineValue: number): number => {
    if (baselineValue === 0) return 0; // Avoid division by zero
    return ((currentValue - baselineValue) / baselineValue) * 100;
  };

  // Function to check if token meets sniper criteria
  const checkSniperRules = (tokenAddress: string, tokenData: any) => {
    // SIMPLIFIED SAFETY CHECK - Use ref as single source of truth for WebSocket callbacks
    const isSystemActive = isSnipingActiveRef.current;
    const currentRules = sniperRulesRef.current;
    
    if (!isSystemActive) {
      console.log(`🛑 SNIPER INACTIVE: Skipping rule check for ${tokenAddress.slice(0, 8)}...`);
      return;
    }
    
    const activeRules = currentRules.filter(rule => rule.isActive);
    if (activeRules.length === 0) {
      console.log(`🛑 NO ACTIVE RULES: Skipping rule check for ${tokenAddress.slice(0, 8)}...`);
      return;
    }
    
    // ENHANCED VALIDATION: Validate token data structure
    if (!tokenData || typeof tokenData !== 'object') {
      console.warn(`❌ Invalid token data for ${tokenAddress.slice(0, 8)}: not an object`);
      return;
    }
    
    console.log(`🔍 CHECKING RULES for ${tokenAddress.slice(0, 8)}... (${activeRules.length} active rules)`);
    
    const tokenInfo = tokenMetadataRef.current.get(tokenAddress);
    
    for (const rule of activeRules) {
      console.log(`🎯 CHECKING RULE: "${rule.name}" (ID: ${rule.id})`);
      console.log(`📋 Rule has ${rule.conditions.length} condition(s):`, rule.conditions.map(c => `${c.field} ${c.operator} ${c.value}`));
      
      // ENHANCED VALIDATION: Validate rule structure
      if (!rule.conditions || !Array.isArray(rule.conditions) || rule.conditions.length === 0) {
        console.warn(`❌ Rule "${rule.name}" has invalid conditions structure`);
        continue;
      }
      
      // First, set baselines for any percentage operators in this rule
      rule.conditions.forEach(condition => {
        if (condition.operator === 'increase%' || condition.operator === 'decrease%') {
          const actualFieldName = mapFieldToDataField(condition.field);
          const fieldValue = tokenData[actualFieldName];
          
          if (fieldValue !== undefined && fieldValue !== null && typeof fieldValue === 'number' && !isNaN(fieldValue)) {
            setRuleBaseline(rule.id, tokenAddress, actualFieldName, fieldValue);
          }
        }
      });
      
      // ENHANCED DEBUGGING: Check each condition individually
      const conditionResults: Array<{condition: LocalSniperCondition, result: boolean, reason: string, actualValue: any}> = [];
      
      const meetsAllConditions = rule.conditions.every((condition, index) => {
        // Map UI field name to actual data field name
        const actualFieldName = mapFieldToDataField(condition.field);
        const fieldValue = tokenData[actualFieldName];
        
        console.log(`🔍 CONDITION ${index + 1}/${rule.conditions.length}: ${condition.field} (${actualFieldName}) ${condition.operator} ${condition.value}`);
        console.log(`   📊 Actual value: ${fieldValue} (type: ${typeof fieldValue})`);
        
        if (fieldValue === undefined || fieldValue === null) {
          const reason = `Field "${actualFieldName}" is ${fieldValue === undefined ? 'undefined' : 'null'} in token data`;
          console.log(`   ❌ FAILED: ${reason}`);
          conditionResults.push({condition, result: false, reason, actualValue: fieldValue});
          return false;
        }
        
        // Handle special string fields (name and symbol)
        if (condition.field === 'name' || condition.field === 'symbol') {
          // For name and symbol, only '=' operator is allowed
          if (condition.operator !== '=') {
            const reason = `Invalid operator '${condition.operator}' for field '${condition.field}'. Only '=' is allowed.`;
            console.warn(`⚠️ ${reason}`);
            conditionResults.push({condition, result: false, reason, actualValue: fieldValue});
            return false;
          }
          
          // Get the string value to compare against (stored as the numeric value converted to string)
          const expectedValue = String(condition.value);
          const actualValue = String(fieldValue).toLowerCase();
          const expectedValueLower = expectedValue.toLowerCase();
          
          // Case-insensitive string comparison
          const result = actualValue === expectedValueLower || actualValue.includes(expectedValueLower);
          const reason = result ? 
            `String match: "${actualValue}" ${actualValue.includes(expectedValueLower) ? 'contains' : 'equals'} "${expectedValueLower}"` :
            `String mismatch: "${actualValue}" does not contain "${expectedValueLower}"`;
          
          console.log(`   ${result ? '✅' : '❌'} ${reason}`);
          conditionResults.push({condition, result, reason, actualValue: fieldValue});
          return result;
        }
        
        // Handle numeric fields with existing logic
        let result = false;
        let reason = '';
        
        switch (condition.operator) {
          case '>': 
            result = fieldValue > condition.value;
            reason = `${fieldValue} > ${condition.value} = ${result}`;
            break;
          case '<': 
            result = fieldValue < condition.value;
            reason = `${fieldValue} < ${condition.value} = ${result}`;
            break;
          case '>=': 
            result = fieldValue >= condition.value;
            reason = `${fieldValue} >= ${condition.value} = ${result}`;
            break;
          case '<=': 
            result = fieldValue <= condition.value;
            reason = `${fieldValue} <= ${condition.value} = ${result}`;
            break;
          case '=': 
            result = fieldValue === condition.value;
            reason = `${fieldValue} === ${condition.value} = ${result}`;
            break;
          case 'increase%': {
            const baseline = getRuleBaseline(rule.id, tokenAddress, actualFieldName);
            if (baseline === null) {
              reason = `No baseline yet for ${actualFieldName} in rule "${rule.name}" - skipping percentage check`;
              console.log(`📊 ${reason}`);
              conditionResults.push({condition, result: false, reason, actualValue: fieldValue});
              return false; // No baseline yet, can't calculate percentage
            }
            const percentageChange = calculatePercentageChange(fieldValue, baseline);
            result = percentageChange >= condition.value;
            reason = `${actualFieldName} changed ${percentageChange.toFixed(2)}% (baseline: ${baseline}, current: ${fieldValue}, target: +${condition.value}%) = ${result}`;
            console.log(`📈 INCREASE% CHECK: ${reason}`);
            break;
          }
          case 'decrease%': {
            const baseline = getRuleBaseline(rule.id, tokenAddress, actualFieldName);
            if (baseline === null) {
              reason = `No baseline yet for ${actualFieldName} in rule "${rule.name}" - skipping percentage check`;
              console.log(`📊 ${reason}`);
              conditionResults.push({condition, result: false, reason, actualValue: fieldValue});
              return false; // No baseline yet, can't calculate percentage
            }
            const percentageChange = calculatePercentageChange(fieldValue, baseline);
            result = Math.abs(percentageChange) >= condition.value && percentageChange < 0;
            reason = `${actualFieldName} changed ${percentageChange.toFixed(2)}% (baseline: ${baseline}, current: ${fieldValue}, target: -${condition.value}%) = ${result}`;
            console.log(`📉 DECREASE% CHECK: ${reason}`);
            break;
          }
          default: 
            result = false;
            reason = `Unknown operator: ${condition.operator}`;
            break;
        }
        
        console.log(`   ${result ? '✅' : '❌'} ${reason}`);
        conditionResults.push({condition, result, reason, actualValue: fieldValue});
        return result;
      });
      
      // ENHANCED DEBUGGING: Show summary of all condition results
      console.log(`📊 RULE "${rule.name}" CONDITION SUMMARY:`);
      conditionResults.forEach((condResult, index) => {
        console.log(`   ${index + 1}. ${condResult.condition.field} ${condResult.condition.operator} ${condResult.condition.value}: ${condResult.result ? '✅ PASS' : '❌ FAIL'}`);
        if (!condResult.result) {
          console.log(`      └─ Reason: ${condResult.reason}`);
        }
      });
      
      const passedConditions = conditionResults.filter(c => c.result).length;
      console.log(`🎯 RULE RESULT: ${passedConditions}/${conditionResults.length} conditions passed. Rule ${meetsAllConditions ? 'MATCHED' : 'FAILED'}`);
      
      if (meetsAllConditions) {
        console.log(`🚨 RULE MATCH: Rule "${rule.name}" matched for ${tokenInfo?.symbol || tokenAddress}`);
        console.log(`📊 Token Data:`, {
          marketCap: tokenData.marketCapUsd,
          liquidity: tokenData.liquidityUsd,
          holders: tokenData.holders,
          buys: tokenData.buys,
          sells: tokenData.sells,
          curvePercentage: tokenData.curvePercentage
        });
        
        // Create rule configuration object
        const ruleConfig = {
          solAmountPerSnipe: rule.solAmountPerSnipe || 0.1,
          slippagePercent: rule.slippagePercent || 35,
          maxTokensToSnipe: rule.maxTokensToSnipe || 5,
          tradingWalletAddresses: rule.tradingWalletAddresses || []
        };
        
        console.log(`✅ EXECUTING TRADE: Rule "${rule.name}" for ${tokenInfo?.symbol || tokenAddress}`);
        executeSniperTrade(rule.id, tokenAddress, tokenInfo || null, ruleConfig, rule.name, rule.conditions);
        break; // Only execute one rule per token
      } else {
        console.log(`❌ RULE FAILED: Rule "${rule.name}" did not match - ${conditionResults.length - passedConditions} condition(s) failed`);
      }
    }
  };

  // Function to execute sniper trade
  const executeSniperTrade = async (
    ruleId: string,
    tokenAddress: string,
    tokenInfo: { name: string; symbol: string } | null,
    ruleConfig: {
      solAmountPerSnipe: number;
      slippagePercent: number;
      maxTokensToSnipe: number;
      tradingWalletAddresses: string[];
    },
    appliedRuleName: string,
    matchedConditions?: LocalSniperCondition[]
  ) => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) {
      console.error('❌ No effective wallet address for executing trade');
      return;
    }

    // CRITICAL: Check if this rule is already executing a trade (prevent race conditions)
    const existingExecution = ruleExecutionLockRef.current.get(ruleId);
    if (existingExecution) {
      console.log(`⏭️ EXECUTION SKIPPED: Rule "${appliedRuleName}" is already executing a trade for another token`);
      return;
    }

    // Create execution lock for this rule
    let resolveExecution!: () => void;
    const executionPromise = new Promise<void>((resolve) => {
      resolveExecution = resolve;
    });
    ruleExecutionLockRef.current.set(ruleId, executionPromise);

    try {
      // Determine which wallets to use based on strategy setting (moved up for limit checking)
      const rule = sniperRules.find(r => r.id === ruleId);
      const strategy = rule?.configurationMetadata?.sniperStrategy || 
                      (rule?.configurationMetadata?.enableSniperMirroring ? 'mirrored' : 'diversified');

      // Check limits based on strategy
      if (strategy === 'individual') {
        // INDIVIDUAL MODE: Check per-wallet limits
        const walletSnipeCounts = ruleWalletSnipeCountRef.current.get(ruleId) || new Map();
        
        // Check if any wallet has reached its individual limit
        let hasWalletReachedLimit = false;
        for (const walletAddress of ruleConfig.tradingWalletAddresses) {
          const walletCount = walletSnipeCounts.get(walletAddress) || 0;
          if (walletCount >= ruleConfig.maxTokensToSnipe) {
            console.log(`⏹️ INDIVIDUAL MODE WALLET LIMIT: Wallet ${walletAddress.slice(0, 8)}... has reached limit ${walletCount}/${ruleConfig.maxTokensToSnipe} tokens`);
            hasWalletReachedLimit = true;
          }
        }
        
        // If all wallets have reached their individual limits, stop the rule
        const allWalletsAtLimit = ruleConfig.tradingWalletAddresses.every(walletAddress => {
          const walletCount = walletSnipeCounts.get(walletAddress) || 0;
          return walletCount >= ruleConfig.maxTokensToSnipe;
        });
        
        if (allWalletsAtLimit) {
          const totalSnipes = Array.from(walletSnipeCounts.values()).reduce((sum, count) => sum + count, 0);
          console.log(`⏹️ INDIVIDUAL MODE RULE COMPLETE: All wallets have reached their individual limits (${totalSnipes} total tokens sniped)`);
          
          // Get rule statistics for completion notification
          const ruleStats = ruleStatistics.get(ruleId) || {triggers: 0, success: 0, failed: 0};
          
          // Auto-disable the rule
          try {
            await sniperRulesService.toggleRuleActive(effectiveWalletAddress, ruleId);
            console.log(`🛑 AUTO-DISABLED RULE: ${appliedRuleName} (all wallets reached individual limits)`);
            
            // Show completion notification with statistics
            showNotification(
              `🎯 Sniper Bot "${appliedRuleName}" completed successfully! ✅ ${totalSnipes} tokens sniped across all wallets, ❌ ${ruleStats.failed} failed attempts. Bot has been automatically stopped.`,
              'success'
            );
            
            // Reload rules to update UI
            await loadSniperRules();
          } catch (error) {
            console.error('Error auto-disabling rule:', error);
          }
          
          return;
        }
      } else {
        // MIRRORED/DIVERSIFIED MODE: Use total count limit
        const currentSnipeCount = ruleSnipeCountRef.current.get(ruleId) || 0;
        const maxTotalSnipes = ruleConfig.maxTokensToSnipe * ruleConfig.tradingWalletAddresses.length;
        
        if (currentSnipeCount >= maxTotalSnipes) {
          console.log(`⏹️ RULE LIMIT REACHED: Rule ${appliedRuleName} has already sniped ${currentSnipeCount}/${maxTotalSnipes} tokens (${ruleConfig.maxTokensToSnipe} per wallet × ${ruleConfig.tradingWalletAddresses.length} wallets)`);
          
          // Get rule statistics for completion notification
          const ruleStats = ruleStatistics.get(ruleId) || {triggers: 0, success: 0, failed: 0};
          
          // Auto-disable the rule
          try {
            await sniperRulesService.toggleRuleActive(effectiveWalletAddress, ruleId);
            console.log(`🛑 AUTO-DISABLED RULE: ${appliedRuleName} (reached max snipes limit)`);
            
            // Show completion notification with statistics
            showNotification(
              `🎯 Sniper Bot "${appliedRuleName}" completed successfully! ✅ ${currentSnipeCount} tokens sniped, ❌ ${ruleStats.failed} failed attempts. Bot has been automatically stopped.`,
              'success'
            );
            
            // Reload rules to update UI
            await loadSniperRules();
          } catch (error) {
            console.error('Error auto-disabling rule:', error);
          }
          
          return;
        }
      }

    // Strategy was already determined above

    // Check for duplicate recent snipes (prevent multiple snipes of same token within 1 minute)
    const now = Date.now();
    const lastSnipeTime = recentlySnipedTokensRef.current.get(tokenAddress);
    if (lastSnipeTime && (now - lastSnipeTime) < DUPLICATE_PREVENTION_TIME) {
      console.log(`⏭️ DUPLICATE PREVENTED: Token ${tokenInfo?.symbol || tokenAddress} was already sniped ${((now - lastSnipeTime) / 1000).toFixed(1)}s ago`);
      return;
    }

    // INDIVIDUAL MODE: Check if this rule has already sniped this token (permanent prevention)
    if (strategy === 'individual') {
      const ruleSnipedTokens = rulePermanentSnipesRef.current.get(ruleId) || new Set();
      if (ruleSnipedTokens.has(tokenAddress)) {
        console.log(`⏭️ INDIVIDUAL MODE DUPLICATE PREVENTED: Rule "${appliedRuleName}" has already sniped ${tokenInfo?.symbol || tokenAddress} - skipping permanently`);
        return;
      }
    }
    
    // ENHANCED DUPLICATE PREVENTION: Check if any wallet already holds this token (only for diversified strategy)
    // Note: Individual strategy has permanent duplicate prevention above, Mirrored strategy allows duplicates since wallets operate together
    if (strategy === 'diversified') {
      console.log(`🔍 DUPLICATE CHECK (Diversified): Checking if any trading wallet already holds ${tokenInfo?.symbol || tokenAddress}...`);
      
      // Check each trading wallet for existing balance of this token
      let hasExistingPosition = false;
      for (const walletAddress of ruleConfig.tradingWalletAddresses) {
        try {
          const tokenBalance = await getTokenBalance(walletAddress, tokenAddress);
          const balance = parseFloat(tokenBalance || '0');
          
          if (balance > 0.000001) { // More than dust amount
            console.log(`⏭️ DUPLICATE PREVENTED: Wallet ${walletAddress.slice(0, 8)}... already holds ${balance} ${tokenInfo?.symbol || tokenAddress}`);
            hasExistingPosition = true;
            break;
          }
        } catch (error) {
          // If we can't check balance, assume no position (continue with trade)
          console.warn(`⚠️ Could not check token balance for wallet ${walletAddress.slice(0, 8)}...:`, error);
        }
      }
      
      if (hasExistingPosition) {
        console.log(`🚫 SKIPPING TRADE: One or more wallets already hold ${tokenInfo?.symbol || tokenAddress} (diversified mode)`);
        return;
      }
    }

    // Mark token as recently sniped
    recentlySnipedTokensRef.current.set(tokenAddress, now);
    let walletsToUse: Set<string>;

    if (strategy === 'mirrored') {
      // Mirrored mode: all wallets buy the same token
      walletsToUse = new Set(ruleConfig.tradingWalletAddresses);
      console.log(`🪞 MIRRORED MODE: All ${walletsToUse.size} wallets will buy ${tokenInfo?.symbol || tokenAddress}`);
    } else if (strategy === 'individual') {
      // Individual mode: all wallets buy independently (like mirrored but with different logic)
      walletsToUse = new Set(ruleConfig.tradingWalletAddresses);
      console.log(`👤 INDIVIDUAL MODE: All ${walletsToUse.size} wallets will buy ${tokenInfo?.symbol || tokenAddress} independently`);
    } else {
      // Diversified mode: round-robin wallet distribution across all trades
      const wallets = ruleConfig.tradingWalletAddresses;
      const currentIndex = walletRotationRef.current.get(ruleId) || 0;
      const selectedWallet = wallets[currentIndex % wallets.length];
      
      // Update rotation index for next trade
      walletRotationRef.current.set(ruleId, currentIndex + 1);
      
      walletsToUse = new Set([selectedWallet]);
      
      console.log(`🎯 DIVERSIFIED MODE: Round-robin selected wallet ${selectedWallet.slice(0, 8)}... (${currentIndex + 1}/${wallets.length}) for ${tokenInfo?.symbol || tokenAddress}`);
    }

    console.log(`🎯 EXECUTING SNIPER TRADE: ${appliedRuleName} → ${tokenInfo?.symbol || tokenAddress} (${walletsToUse.size} wallets)`);
    console.log(`📋 TRADE CONFIG: ${ruleConfig.solAmountPerSnipe} SOL/snipe | ${ruleConfig.slippagePercent}% slippage | ${ruleConfig.maxTokensToSnipe} max tokens`);
    
    // CRITICAL: Validate slippage configuration
    if (!ruleConfig.slippagePercent || ruleConfig.slippagePercent <= 0) {
      console.error(`❌ INVALID SLIPPAGE: Rule "${appliedRuleName}" has invalid slippage: ${ruleConfig.slippagePercent}%`);
      return;
    }

    let successCount = 0;
    let failCount = 0;
    const toastIds = new Map<string, string>(); // Track toast IDs per wallet

    for (const walletAddress of walletsToUse) {
      let toastId: string | undefined;
      
      try {
        console.log(`⏳ Executing trade for wallet ${walletAddress.slice(0, 8)}...`);
        
        // Execute the trade using the trade engine with EXACT slippage from rule
        console.log(`🎯 Executing trade with slippage: ${ruleConfig.slippagePercent}% (from rule "${appliedRuleName}")`);
        const result = await tradeEngine.executeSwap({
          inputMint: 'So11111111111111111111111111111111111111112', // SOL mint
          outputMint: tokenAddress,
          amount: ruleConfig.solAmountPerSnipe.toString(),
          userWalletAddress: effectiveWalletAddress,
          tradingWalletAddress: walletAddress,
          slippagePercent: ruleConfig.slippagePercent.toString() // CRITICAL: Use exact slippage from rule
        });

        // ONLY show loading notification AFTER we've successfully started the transaction
        // This prevents notifications for pre-transaction failures like "no route available"
        toastId = showSniperNotification(
          'loading',
          appliedRuleName,
          tokenInfo?.symbol || 'Unknown',
          tokenInfo?.name || 'Unknown',
          ruleConfig.solAmountPerSnipe,
          walletAddress,
          undefined,
          undefined,
          matchedConditions
        ) as string;
        toastIds.set(walletAddress, toastId);

        console.log(`✅ TRADE SUCCESS! Wallet: ${walletAddress.slice(0, 8)}... | Signature: ${result.signature}`);
        successCount++;

        // INDIVIDUAL MODE: Mark this token as permanently sniped for this rule
        if (strategy === 'individual') {
          if (!rulePermanentSnipesRef.current.has(ruleId)) {
            rulePermanentSnipesRef.current.set(ruleId, new Set());
          }
          rulePermanentSnipesRef.current.get(ruleId)!.add(tokenAddress);
          console.log(`🔒 INDIVIDUAL MODE: Permanently marked ${tokenInfo?.symbol || tokenAddress} as sniped for rule "${appliedRuleName}"`);
        }

        // Dismiss loading toast and show success
        if (toastId) {
          hideNotification(toastId, 'loading');
        }
        
        showSniperNotification(
          'success',
          appliedRuleName,
          tokenInfo?.symbol || 'Unknown',
          tokenInfo?.name || 'Unknown',
          ruleConfig.solAmountPerSnipe,
          walletAddress,
          result.signature,
          undefined,
          matchedConditions
        );

                          // Record successful trade
        await sniperRulesService.recordSniperTrade(
          effectiveWalletAddress,
          ruleId,
          tokenAddress,
          tokenInfo?.name || 'Unknown',
          tokenInfo?.symbol || 'Unknown',
          walletAddress,
          ruleConfig.solAmountPerSnipe,
          ruleConfig.slippagePercent,
          result.signature,
          'success',
          undefined,
          matchedConditions
        );

        // Create sniper position for auto-sell tracking
        const rule = sniperRules.find(r => r.id === ruleId);
        const isAutoSellEnabled = rule?.configurationMetadata?.enableAutoSell || rule?.enableAutoSell;
        
        if (rule && isAutoSellEnabled) {
          const autoSellConfig = {
            enabled: true,
            profitPercent: rule.configurationMetadata?.autoSellProfitPercent || rule.autoSellProfitPercent || 50,
            lossPercent: rule.configurationMetadata?.autoSellLossPercent || rule.autoSellLossPercent || 25,
            positionPercent: rule.configurationMetadata?.autoSellPositionPercent || rule.autoSellPositionPercent || 100
          };

          console.log(`🎯 Creating auto-sell position for ${tokenInfo?.symbol} with config:`, autoSellConfig);

          try {
            const position = await sniperPositionsService.createSniperPosition(
              effectiveWalletAddress,
              ruleId,
              appliedRuleName,
              tokenAddress,
              tokenInfo?.symbol || 'Unknown',
              tokenInfo?.name || 'Unknown',
              walletAddress,
              ruleConfig.solAmountPerSnipe,
              result.signature,
              autoSellConfig
            );

            if (position) {
              console.log(`✅ Created auto-sell position for ${tokenInfo?.symbol} (${autoSellConfig.profitPercent}% profit / -${autoSellConfig.lossPercent}% loss)`);
            } else {
              console.error(`❌ Failed to create auto-sell position for ${tokenInfo?.symbol}`);
            }
          } catch (error) {
            console.error(`❌ Error creating auto-sell position for ${tokenInfo?.symbol}:`, error);
          }
        } else {
          console.log(`⏭️ Auto-sell disabled for ${tokenInfo?.symbol}, no position created`);
        }

      } catch (error: any) {
        console.error(`❌ TRADE FAILED! Wallet: ${walletAddress.slice(0, 8)}... | Error:`, error);
        failCount++;

        // Get a user-friendly error message
        let errorMessage = 'Unknown error';
        if (error instanceof Error) {
          errorMessage = error.message;
        }

        // Check if this is a pre-transaction error (should not be recorded in history)
        const isPreTransactionError = 
          errorMessage.includes('TOKEN_NOT_TRADABLE') ||
          errorMessage.includes('This token cannot be traded on Jupiter') ||
          errorMessage.includes('Token not found in Jupiter') ||
          errorMessage.includes('This token is too new or not recognized') ||
          errorMessage.includes('No route found') ||
          errorMessage.includes('No route available for this token') ||
          errorMessage.includes('not have enough liquidity') ||
          errorMessage.includes('insufficient liquidity') ||
          errorMessage.includes('liquidity') ||
          errorMessage.includes('Failed to execute swap: No route available') ||
          errorMessage.includes('not tradable') ||
          errorMessage.includes('TradeError: Failed to execute swap') ||
          errorMessage.includes('Jupiter API error') ||
          errorMessage.includes('Bad Request') ||
          errorMessage.includes('Method Not Allowed') ||
          errorMessage.includes('slippage tolerance') ||
          errorMessage.includes('Slippage tolerance') ||
          errorMessage.includes('slippage tolerance exceeded') ||
          errorMessage.includes('Slippage tolerance exceeded') ||
          errorMessage.includes('Price impact too high') ||
          errorMessage.includes('price impact') ||
          errorMessage.includes('insufficient funds') ||
          errorMessage.includes('insufficient balance') ||
          errorMessage.includes('Insufficient funds') ||
          errorMessage.includes('Insufficient balance') ||
          errorMessage.includes('INSUFFICIENT_BALANCE') ||
          errorMessage.includes('NO_ROUTE_FOUND') ||
          errorMessage.includes('COULD_NOT_FIND_ANY_ROUTE') ||
          errorMessage.includes('missing token decimals') ||
          errorMessage.includes('TOKEN_METADATA_MISSING') ||
          (error.type && (
            error.type === 'TOKEN_NOT_FOUND' || 
            error.type === 'NO_ROUTE_FOUND' ||
            error.type === 'JUPITER_ERROR' ||
            error.type === 'NO_LIQUIDITY' ||
            error.type === 'SLIPPAGE_EXCEEDED' ||
            error.type === 'PRICE_IMPACT_TOO_HIGH' ||
            error.type === 'INSUFFICIENT_BALANCE'
          ));

        // Check if this is a confirmation timeout (transaction might still succeed)
        const isConfirmationTimeout = 
          errorMessage.includes('Transaction confirmation timeout') ||
          errorMessage.includes('timeout after 30 seconds');

        // Check if this is an actual on-chain failure (transaction was confirmed but failed)
        const isOnChainFailure = 
          errorMessage.includes('Transaction failed on-chain') ||
          errorMessage.includes('failed on-chain');

        // Only dismiss loading toast if it was shown (transaction actually started)
        if (toastId) {
          hideNotification(toastId, 'loading');
        }

        // Handle different error types appropriately
        if (isConfirmationTimeout) {
          // Timeout errors: Don't count as success or failure, just log
          console.log(`⏰ CONFIRMATION TIMEOUT: ${tokenInfo?.symbol || tokenAddress} - Transaction may still be processing`);
          console.log(`   └─ 🔄 Signature may still confirm: Check Solscan later`);
          
          // Show a warning notification but don't record as failed
          showSniperNotification(
            'error',
            appliedRuleName,
            tokenInfo?.symbol || 'Unknown',
            tokenInfo?.name || 'Unknown',
            ruleConfig.solAmountPerSnipe,
            walletAddress,
            undefined,
            'Transaction timeout - may still be processing',
            matchedConditions
          );
          
          // Don't count timeout as failure since transaction might still succeed
          failCount--; // Undo the failCount increment
          
        } else if (isOnChainFailure) {
          // On-chain failures: Count as actual failures and record
          console.log(`💥 ON-CHAIN FAILURE: ${tokenInfo?.symbol || tokenAddress} - Transaction confirmed but failed`);
          
          await sniperRulesService.recordSniperTrade(
            effectiveWalletAddress,
            ruleId,
            tokenAddress,
            tokenInfo?.name || 'Unknown',
            tokenInfo?.symbol || 'Unknown',
            walletAddress,
            ruleConfig.solAmountPerSnipe,
            ruleConfig.slippagePercent,
            undefined,
            'failed',
            errorMessage,
            matchedConditions
          );
          
          showSniperNotification(
            'error',
            appliedRuleName,
            tokenInfo?.symbol || 'Unknown',
            tokenInfo?.name || 'Unknown',
            ruleConfig.solAmountPerSnipe,
            walletAddress,
            undefined,
            errorMessage,
            matchedConditions
          );
          
        } else if (!isPreTransactionError) {
          // Other transaction failures: Record as failed
          await sniperRulesService.recordSniperTrade(
            effectiveWalletAddress,
            ruleId,
            tokenAddress,
            tokenInfo?.name || 'Unknown',
            tokenInfo?.symbol || 'Unknown',
            walletAddress,
            ruleConfig.solAmountPerSnipe,
            ruleConfig.slippagePercent,
            undefined,
            'failed',
            errorMessage,
            matchedConditions
          );
        } else {
          // Pre-transaction errors: Don't record or count as failures
          console.log(`⏭️ PRE-TRANSACTION ERROR (filtered out): ${errorMessage}`);
          console.log(`🔄 Token ${tokenInfo?.symbol || tokenAddress} - Jupiter routing/liquidity issue, skipping notification and database record`);
          
          // Don't count pre-transaction errors as failures
          failCount--; // Undo the failCount increment
          
          // Show specific error type for debugging
          if (errorMessage.includes('No route available') || errorMessage.includes('not have enough liquidity') || errorMessage.includes('insufficient liquidity') || errorMessage.includes('liquidity')) {
            console.log(`   └─ 🚫 LIQUIDITY/ROUTING: Token has insufficient liquidity or no available routes`);
          } else if (errorMessage.includes('slippage tolerance') || errorMessage.includes('Price impact too high')) {
            console.log(`   └─ 🚫 SLIPPAGE EXCEEDED: Trade would exceed slippage tolerance (${ruleConfig.slippagePercent}%)`);
          } else if (errorMessage.includes('insufficient funds') || errorMessage.includes('insufficient balance') || errorMessage.includes('INSUFFICIENT_BALANCE')) {
            console.log(`   └─ 🚫 INSUFFICIENT FUNDS: Trading wallet lacks sufficient balance for this trade`);
          } else if (errorMessage.includes('Method Not Allowed') || errorMessage.includes('Bad Request')) {
            console.log(`   └─ 🚫 JUPITER API ERROR: API endpoint or method issue`);
          } else if (errorMessage.includes('TOKEN_NOT_TRADABLE')) {
            console.log(`   └─ 🚫 TOKEN RESTRICTION: Token is not tradable`);
          } else if (errorMessage.includes('missing token decimals') || errorMessage.includes('TOKEN_METADATA_MISSING')) {
            console.log(`   └─ 🚫 TOKEN METADATA: Token metadata not yet available`);
          } else {
            console.log(`   └─ 🚫 OTHER PRE-TRANSACTION: ${errorMessage.substring(0, 100)}...`);
          }
        }
      }
    }
    
    // Update snipe counts based on strategy
    if (strategy === 'individual') {
      // INDIVIDUAL MODE: Update per-wallet counts
      if (!ruleWalletSnipeCountRef.current.has(ruleId)) {
        ruleWalletSnipeCountRef.current.set(ruleId, new Map());
      }
      const walletSnipeCounts = ruleWalletSnipeCountRef.current.get(ruleId)!;
      
      // Update count for each wallet that successfully traded
      for (const walletAddress of walletsToUse) {
        if (successCount > 0) { // Only if there were successful trades
          const currentWalletCount = walletSnipeCounts.get(walletAddress) || 0;
          walletSnipeCounts.set(walletAddress, currentWalletCount + 1);
          console.log(`📊 INDIVIDUAL MODE: Wallet ${walletAddress.slice(0, 8)}... now has ${currentWalletCount + 1}/${ruleConfig.maxTokensToSnipe} tokens`);
        }
      }
      
      // Check if all wallets have reached their individual limits
      const allWalletsAtLimit = ruleConfig.tradingWalletAddresses.every(walletAddress => {
        const walletCount = walletSnipeCounts.get(walletAddress) || 0;
        return walletCount >= ruleConfig.maxTokensToSnipe;
      });
      
      if (allWalletsAtLimit) {
        const totalSnipes = Array.from(walletSnipeCounts.values()).reduce((sum, count) => sum + count, 0);
        console.log(`⏹️ INDIVIDUAL MODE RULE COMPLETE: All wallets have reached their individual limits (${totalSnipes} total tokens sniped)`);
        
        try {
          // Get rule statistics for completion notification before disabling
          const ruleStats = ruleStatistics.get(ruleId) || {triggers: 0, success: 0, failed: 0};
          
          await sniperRulesService.toggleRuleActive(effectiveWalletAddress, ruleId);
          console.log(`🛑 AUTO-DISABLED RULE: ${appliedRuleName} (all wallets reached individual limits)`);
          
          // Show completion notification with updated statistics
          showNotification(
            `🎯 Sniper Bot "${appliedRuleName}" completed successfully! ✅ ${totalSnipes} tokens sniped across all wallets, ❌ ${ruleStats.failed} failed attempts. Bot has been automatically stopped.`,
            'success'
          );
          
          // Reload rules to update UI
          await loadSniperRules();
        } catch (error) {
          console.error('Error auto-disabling rule:', error);
        }
      }
    } else {
      // MIRRORED/DIVERSIFIED MODE: Use total count limit
      const currentSnipeCount = ruleSnipeCountRef.current.get(ruleId) || 0;
      const maxTotalSnipes = ruleConfig.maxTokensToSnipe * ruleConfig.tradingWalletAddresses.length;
      const actualSnipesToAdd = successCount; // Each successful wallet = 1 snipe
      
      // Update snipe count for this rule (only count successful snipes)
      const newCount = currentSnipeCount + actualSnipesToAdd;
      ruleSnipeCountRef.current.set(ruleId, newCount);
      
      console.log(`📊 SNIPER TRADE SUMMARY: ${successCount} success, ${failCount} failed out of ${walletsToUse.size} wallets`);
      console.log(`📋 Rule ${appliedRuleName}: ${newCount}/${maxTotalSnipes} snipes completed (${ruleConfig.maxTokensToSnipe} per wallet × ${ruleConfig.tradingWalletAddresses.length} wallets, +${actualSnipesToAdd} snipes)`);
      
      // Auto-disable rule if limit reached
      if (newCount >= maxTotalSnipes) {
        try {
          // Get rule statistics for completion notification before disabling
          const ruleStats = ruleStatistics.get(ruleId) || {triggers: 0, success: 0, failed: 0};
          
          await sniperRulesService.toggleRuleActive(effectiveWalletAddress, ruleId);
          console.log(`🛑 AUTO-DISABLED RULE: ${appliedRuleName} (reached max snipes limit: ${newCount}/${maxTotalSnipes})`);
          
          // Show completion notification with updated statistics (including this latest batch)
          const finalSuccessCount = ruleStats.success + successCount;
          const finalFailedCount = ruleStats.failed + failCount;
          showNotification(
            `🎯 Sniper Bot "${appliedRuleName}" completed successfully! ✅ ${newCount} tokens sniped, ❌ ${finalFailedCount} failed attempts. Bot has been automatically stopped.`,
            'success'
          );
          
          // Reload rules to update UI
          await loadSniperRules();
        } catch (error) {
          console.error('Error auto-disabling rule:', error);
        }
      }
    }
    } catch (error) {
      console.error('❌ Error executing sniper trade:', error);
    } finally {
      // Always clean up the execution lock for this rule
      ruleExecutionLockRef.current.delete(ruleId);
      resolveExecution();
      console.log(`🔓 Released execution lock for rule "${appliedRuleName}"`);
    }
  };

  // Load trading wallets
  const loadTradingWallets = async () => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) return;
    
    setIsLoadingWallets(true);
    try {
      // FIXED: Use built-in parallel balance loading for much better performance
      console.log(`💼 Loading trading wallets with balances in parallel...`);
      const walletsWithBalances = await getUserTradingWallets(effectiveWalletAddress, { 
        includeBalances: true  // ← This does parallel loading internally
      });
      
      setTradingWallets(walletsWithBalances);
      
      // Extract balances from the wallets data 
      const balances = new Map<string, number>();
      walletsWithBalances.forEach(wallet => {
        if (wallet.balance !== undefined) {
          balances.set(wallet.public_key, wallet.balance);
        } else {
          balances.set(wallet.public_key, 0);
        }
      });
      setWalletBalances(balances);
      
      console.log(`✅ Loaded ${walletsWithBalances.length} trading wallets with balances in parallel (much faster!)`);
      
    } catch (error) {
      console.error('Error loading trading wallets:', error);
      
      // Fallback: Load wallets without balances if parallel loading fails
      try {
        console.log(`🔄 Falling back to loading wallets without balances...`);
        const wallets = await getUserTradingWallets(effectiveWalletAddress);
        setTradingWallets(wallets);
        
        // Set all balances to 0 as fallback
        const balances = new Map<string, number>();
        wallets.forEach(wallet => {
          balances.set(wallet.public_key, 0);
        });
        setWalletBalances(balances);
        
        console.log(`⚠️ Loaded ${wallets.length} trading wallets without balances (fallback mode)`);
      } catch (fallbackError) {
        console.error('Error in fallback wallet loading:', fallbackError);
      }
    } finally {
      setIsLoadingWallets(false);
    }
  };

  // Refresh just the trading wallet balances (fast refresh for wallet switches)
  const refreshTradingWalletBalances = async () => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress || tradingWallets.length === 0) return;
    
    console.log(`🔄 Refreshing trading wallet balances for ${tradingWallets.length} wallets...`);
    
    try {
      // Refresh balances in parallel using Promise.all for speed
      const balancePromises = tradingWallets.map(async (wallet) => {
        try {
          const balanceInLamports = await getBalance(wallet.public_key);
          return {
            address: wallet.public_key,
            balance: balanceInLamports / 1_000_000_000 // Convert to SOL
          };
        } catch (error) {
          console.error(`Error refreshing balance for wallet ${wallet.public_key}:`, error);
          return {
            address: wallet.public_key,
            balance: walletBalances.get(wallet.public_key) || 0 // Keep existing balance on error
          };
        }
      });

      const balanceResults = await Promise.all(balancePromises);
      
      // Update the balance map
      const newBalances = new Map<string, number>();
      balanceResults.forEach(result => {
        newBalances.set(result.address, result.balance);
      });
      
      setWalletBalances(newBalances);
      console.log(`✅ Refreshed balances for ${balanceResults.length} trading wallets`);
      
    } catch (error) {
      console.error('Error refreshing trading wallet balances:', error);
    }
  };

  // Load sniper rules
  const loadSniperRules = async () => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) return;
    
    setIsLoadingRules(true);
    try {
      const rules = await sniperRulesService.getUserSniperRules(effectiveWalletAddress);
      
      // Convert to local format
      const localRules: LocalSniperRule[] = rules.map(rule => ({
        id: rule.id,
        name: rule.name,
        isActive: rule.is_active,
        conditions: rule.conditions.map(c => ({
          field: c.field === 'txAmount' ? 'tokensBought' : c.field as LocalSniperCondition['field'],
          operator: c.operator,
          value: c.value
        })),
        createdAt: rule.created_at,
        solAmountPerSnipe: rule.sol_amount_per_snipe,
        maxTokensToSnipe: rule.max_tokens_to_snipe,
        slippagePercent: rule.slippage_percent,
        tradingWalletAddresses: rule.trading_wallet_addresses,
        totalSolBudget: rule.total_sol_budget,
        configurationMetadata: rule.configuration_metadata,
        enableSniperMirroring: rule.configuration_metadata?.enableSniperMirroring ?? false,
        sniperStrategy: rule.configuration_metadata?.sniperStrategy || 
                       (rule.configuration_metadata?.enableSniperMirroring ? 'mirrored' : 'diversified')
      }));
      
      setSniperRules(localRules);
      
      // FIXED: Don't automatically manage isSnipingActive here - let handleRuleToggle manage it
      // This was causing state conflicts
      
      // FIXED: Initialize sniping state on first load if there are active rules
      const hasActiveRules = localRules.some(rule => rule.isActive);
      const currentSnipingState = isSnipingActive;
      
      // Check if we should keep monitoring due to active auto-sell positions
      let shouldStayActive = hasActiveRules;
      if (!hasActiveRules) {
        const effectiveWalletAddress = getEffectiveWalletAddress();
        if (effectiveWalletAddress) {
          try {
            const hasActivePositions = await sniperPositionsService.hasActiveAutoSellPositions(effectiveWalletAddress);
            if (hasActivePositions) {
              shouldStayActive = true;
              console.log('🎯 Keeping sniper bot active due to active auto-sell positions');
            }
          } catch (error) {
            console.error('Error checking active auto-sell positions:', error);
          }
        }
      }

      // Only update state if it's different to avoid unnecessary updates
      if (shouldStayActive !== currentSnipingState) {
        console.log(`🔄 INITIALIZING SNIPING STATE: ${currentSnipingState} → ${shouldStayActive}`);
        setIsSnipingActive(shouldStayActive);
        // Also sync the ref immediately
        isSnipingActiveRef.current = shouldStayActive;
      }
      
      const activeCount = localRules.filter(r => r.isActive).length;
      if (activeCount > 0) {
        console.log(`🎯 SNIPER RULES LOADED: ${activeCount} active rule(s)`);
        
        // Log each active rule's actual configuration
        localRules.filter(r => r.isActive).forEach(rule => {
          console.log(`📋 Active Bot: "${rule.name}"`);
          console.log(`   - SOL per snipe: ${rule.solAmountPerSnipe || 'NOT CONFIGURED'}`);
          console.log(`   - Slippage: ${rule.slippagePercent || 'NOT CONFIGURED'}%`);
          console.log(`   - Max tokens: ${rule.maxTokensToSnipe || 'NOT CONFIGURED'}`);
          console.log(`   - Wallets: ${rule.tradingWalletAddresses?.length || 'NOT CONFIGURED'}`);
        });
      } else {
        console.log(`⏹️ SNIPER RULES LOADED: No active rules`);
      }
    } catch (error) {
      console.error('Error loading sniper rules:', error);
    } finally {
      setIsLoadingRules(false);
    }
  };

  // Handle rule save
  const handleRuleSave = async (name: string, conditions: Omit<SniperCondition, 'id' | 'rule_id' | 'created_at'>[]) => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) return;

    try {
      if (editingRule) {
        // Update existing rule
        await sniperRulesService.updateSniperRule(
          effectiveWalletAddress,
          editingRule.id,
          { name, conditions }
        );
      } else {
        // Create new rule
        await sniperRulesService.createSniperRule(
          effectiveWalletAddress,
          name,
          conditions
        );
      }

      // Reload rules and statistics
      await loadSniperRules();
      await loadRuleStatistics();
      
      // Close modal
      setShowRuleModal(false);
      setEditingRule(null);
    } catch (error) {
      console.error('Error saving rule:', error);
      throw error; // Re-throw to show error in modal
    }
  };

  // Handle rule toggle
  const handleRuleToggle = async (ruleId: string) => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) return;

    const rule = sniperRules.find(r => r.id === ruleId);
    if (!rule) return;

    // Calculate strategy for this rule (needed for logging)
    const strategy = rule.configurationMetadata?.sniperStrategy || 
                    (rule.configurationMetadata?.enableSniperMirroring ? 'mirrored' : 'diversified');

    if (!rule.isActive) {
      // User is trying to START a rule - validate EVERYTHING
      
      // CRITICAL: Check if rule has complete configuration
      if (!rule.solAmountPerSnipe || 
          !rule.slippagePercent || 
          !rule.maxTokensToSnipe ||
          !rule.tradingWalletAddresses ||
          rule.tradingWalletAddresses.length === 0) {
        
        console.error('🚫 Cannot start sniper bot: Incomplete configuration');
        console.error(`Bot "${rule.name}" missing:`, {
          solAmountPerSnipe: !rule.solAmountPerSnipe,
          slippagePercent: !rule.slippagePercent,
          maxTokensToSnipe: !rule.maxTokensToSnipe,
          tradingWalletAddresses: !rule.tradingWalletAddresses || rule.tradingWalletAddresses.length === 0
        });
        console.error('💡 Fix: Edit this bot and configure SOL amount, slippage, max tokens, and trading wallets');
        showNotification('🚫 Cannot start bot: Incomplete configuration. Please edit the bot to configure SOL amount, slippage, max tokens, and trading wallets.', 'error');
        return;
      }

      // Validate wallet balances using the same logic as creation
      const configuredWallets = new Set(rule.tradingWalletAddresses);
      const issues: string[] = [];
      let hasInvalidWallets = false;
      
      for (const walletAddress of configuredWallets) {
        const wallet = tradingWallets.find(w => w.public_key === walletAddress);
        if (!wallet || !wallet.is_active) {
          issues.push(`Wallet ${walletAddress.slice(0, 6)}... is inactive or not found`);
          hasInvalidWallets = true;
          continue;
        }
        
        const balance = walletBalances.get(walletAddress) || 0;
        
        // Calculate required balance per wallet based on strategy
        let requiredBalance: number;
        if (strategy === 'mirrored' || strategy === 'individual') {
          // Mirrored/Individual mode: Each wallet needs enough for ALL trades
          requiredBalance = rule.solAmountPerSnipe * rule.maxTokensToSnipe;
        } else {
          // Diversified mode: Each wallet needs enough for its share of total tokens
          const baseTokensPerWallet = Math.floor(rule.maxTokensToSnipe / configuredWallets.size);
          const remainingTokens = rule.maxTokensToSnipe % configuredWallets.size;
          const walletIndex = Array.from(configuredWallets).indexOf(walletAddress);
          const tokensForThisWallet = baseTokensPerWallet + (walletIndex < remainingTokens ? 1 : 0);
          requiredBalance = rule.solAmountPerSnipe * tokensForThisWallet;
        }

        if (balance < requiredBalance) {
          let tokensForThisWallet: number;
          
          if (strategy === 'mirrored' || strategy === 'individual') {
            tokensForThisWallet = rule.maxTokensToSnipe;
          } else {
            // Diversified: calculate exact tokens for this wallet
            const baseTokensPerWallet = Math.floor(rule.maxTokensToSnipe / configuredWallets.size);
            const remainingTokens = rule.maxTokensToSnipe % configuredWallets.size;
            const walletIndex = Array.from(configuredWallets).indexOf(walletAddress);
            tokensForThisWallet = baseTokensPerWallet + (walletIndex < remainingTokens ? 1 : 0);
          }
          
          issues.push(`${wallet.name}: has ${balance.toFixed(3)} SOL, needs ${requiredBalance.toFixed(3)} SOL for ${tokensForThisWallet} tokens (${strategy} mode)`);
          hasInvalidWallets = true;
        }
      }

      if (hasInvalidWallets) {
        console.error(`🚫 Cannot start sniper bot: Wallet validation failed`);
        console.error('Issues found:', issues);
        const errorMessage = `🚫 Cannot start bot: ${issues.slice(0, 2).join('; ')}${issues.length > 2 ? ` (+${issues.length - 2} more issues)` : ''}`;
        showNotification(errorMessage, 'error');
        return;
      }

      console.log(`✅ Starting sniper bot "${rule.name}" with saved configuration:`);
      console.log(`   - SOL per snipe: ${rule.solAmountPerSnipe}`);
      console.log(`   - Slippage: ${rule.slippagePercent}%`);
      console.log(`   - Max tokens: ${rule.maxTokensToSnipe}`);
      console.log(`   - Wallets: ${configuredWallets.size}`);
      console.log(`   - Strategy: ${strategy}`);
      
              // Reset snipe counter and wallet rotation for this rule when starting
        ruleSnipeCountRef.current.set(ruleId, 0);
        walletRotationRef.current.set(ruleId, 0);
        
        // Clear any existing baselines for this rule to start fresh
        ruleBaselinesRef.current.delete(ruleId);
        
        // INDIVIDUAL MODE: Clear permanently sniped tokens and per-wallet counts for this rule when restarting
        if (strategy === 'individual') {
          rulePermanentSnipesRef.current.delete(ruleId);
          ruleWalletSnipeCountRef.current.delete(ruleId);
          console.log(`🔄 INDIVIDUAL MODE: Cleared permanently sniped tokens and per-wallet counts for rule "${rule.name}" - can snipe all tokens again`);
        }
        
        const totalSnipesForRule = rule.maxTokensToSnipe * configuredWallets.size;
        console.log(`🔄 Reset snipe counter for rule "${rule.name}" to 0/${totalSnipesForRule} (${rule.maxTokensToSnipe} per wallet × ${configuredWallets.size} wallets)`);
        console.log(`🔄 Reset wallet rotation for rule "${rule.name}" to start from first wallet`);
        console.log(`🔄 Cleared baselines for rule "${rule.name}" - percentage operators will establish new baselines`);
    }

    try {
      // Toggle the rule in the database
      await sniperRulesService.toggleRuleActive(effectiveWalletAddress, ruleId);
      
      // Reload rules to get fresh data
      await loadSniperRules();
      await loadRuleStatistics();
      
      // FIXED: Get the updated rules and properly set the sniping active state
      const updatedRules = await sniperRulesService.getUserSniperRules(effectiveWalletAddress);
      const hasActiveRules = updatedRules.some(rule => rule.is_active);
      
      // Check if we should keep monitoring due to active auto-sell positions
      let shouldStayActive = hasActiveRules;
      if (!hasActiveRules) {
        const hasActivePositions = await sniperPositionsService.hasActiveAutoSellPositions(effectiveWalletAddress);
        if (hasActivePositions) {
          shouldStayActive = true;
          console.log('🎯 Keeping sniper bot active due to active auto-sell positions');
        }
      }
      
      // CRITICAL: Set the state and immediately sync the ref
      setIsSnipingActive(shouldStayActive);
      // Force immediate ref sync - don't wait for useEffect
      isSnipingActiveRef.current = shouldStayActive;
      
      console.log(`🔄 STATE SYNC: isSnipingActive = ${hasActiveRules}, ref = ${hasActiveRules}`);
      
      // Show notification for bot start/stop
      const toggledRule = updatedRules.find(r => r.id === ruleId);
      if (toggledRule) {
        if (toggledRule.is_active) {
          // Bot started
          showNotification(
            `Sniper Bot "${toggledRule.name}" started successfully! Now monitoring for opportunities.`,
            'success'
          );
          console.log(`🎯 SNIPER SYSTEM ACTIVATED - ${updatedRules.filter(r => r.is_active).length} active rule(s)`);
          console.log(`🔍 System will now check rules against tracked tokens`);
        } else {
          // Bot stopped
          showNotification(
            `Sniper Bot "${toggledRule.name}" stopped successfully. No longer monitoring.`,
            'success'
          );
          console.log(`⏹️ SNIPER SYSTEM DEACTIVATED - No active rules`);
          console.log(`🛑 Rule checking stopped - tokens will be tracked but no trades will execute`);
        }
      }
      
      if (hasActiveRules) {
        console.log(`🎯 SNIPER SYSTEM STATUS: ${updatedRules.filter(r => r.is_active).length} active rule(s)`);
      }
    } catch (error) {
      console.error('Error toggling rule:', error);
    }
  };

  // Handle rule delete
  const handleRuleDelete = async (ruleId: string) => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) return;

    // Find the rule to get its name for the confirmation dialog
    const rule = sniperRules.find(r => r.id === ruleId);
    if (!rule) return;

    // Set the rule to delete and show confirmation dialog
    setRuleToDelete({
      id: rule.id,
      name: rule.name,
      isActive: rule.isActive
    });
    setShowDeleteConfirm(true);
  };

  // Confirm and execute rule deletion
  const confirmRuleDelete = async () => {
    if (!ruleToDelete) return;
    
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) return;

    try {
      console.log(`🗑️ Deleting sniper bot: ${ruleToDelete.name} (${ruleToDelete.id})`);
      
      // Show loading notification
      const loadingToastId = showNotification(
        `Deleting sniper bot "${ruleToDelete.name}"...`,
        'loading'
      );
      
      const deleteResult = await sniperRulesService.deleteSniperRule(effectiveWalletAddress, ruleToDelete.id);
      
      console.log('Delete result:', deleteResult);
      
      if (deleteResult === false) {
        throw new Error('Delete operation returned false');
      }
      
      // Hide loading notification
      if (loadingToastId) {
        hideNotification(loadingToastId as string, 'loading');
      }
      
      // Show success notification
      showNotification(
        `✅ Sniper bot "${ruleToDelete.name}" has been successfully deleted.`,
        'success'
      );
      
      console.log(`✅ Successfully deleted sniper bot: ${ruleToDelete.name}`);
      
      // Close confirmation dialog
      setShowDeleteConfirm(false);
      setRuleToDelete(null);
      
      // Reload rules and statistics
      console.log('🔄 Reloading rules after deletion...');
      await loadSniperRules();
      await loadRuleStatistics();
      console.log('🔄 Rules reloaded successfully');
      
    } catch (error) {
      console.error('❌ Error deleting rule:', error);
      
      // Show error notification with more details
      showNotification(
        `❌ Failed to delete sniper bot "${ruleToDelete.name}": ${error instanceof Error ? error.message : 'Unknown error'}`,
        'error'
      );
      
      // Close confirmation dialog even on error
      setShowDeleteConfirm(false);
      setRuleToDelete(null);
    }
  };

  // Cancel rule deletion
  const cancelRuleDelete = () => {
    setShowDeleteConfirm(false);
    setRuleToDelete(null);
  };

  // Load trade history
    const loadTradeHistory = async () => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) return;

    setIsLoadingTradeHistory(true);
    try {
      const [history, autoSells] = await Promise.all([
        sniperRulesService.getUserTradeHistory(effectiveWalletAddress, 100),
        sniperPositionsService.getAutoSellTransactions(effectiveWalletAddress)
      ]);
      setTradeHistory(history);
      setAutoSellTransactions(autoSells);
    } catch (error) {
      console.error('Error loading trade history:', error);
    } finally {
      setIsLoadingTradeHistory(false);
    }
  };

  // Load rule statistics from trade history
  const loadRuleStatistics = async () => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) return;

    try {
      // Get all trade history to calculate statistics
      const allHistory = await sniperRulesService.getUserTradeHistory(effectiveWalletAddress, 1000);
      
      // Group by rule_id and calculate stats
      const statsMap = new Map<string, {triggers: number, success: number, failed: number}>();
      
      allHistory.forEach(trade => {
        if (!trade.rule_id) return; // Skip trades without rule_id
        
        const ruleId = trade.rule_id;
        const currentStats = statsMap.get(ruleId) || {triggers: 0, success: 0, failed: 0};
        
        // Count total triggers (all non-pending trades)
        if (trade.status !== 'pending') {
          currentStats.triggers++;
        }
        
        // Count successes and failures
        if (trade.status === 'success') {
          currentStats.success++;
        } else if (trade.status === 'failed') {
          currentStats.failed++;
        }
        
        statsMap.set(ruleId, currentStats);
      });
      
      setRuleStatistics(statsMap);
      console.log(`📊 Loaded statistics for ${statsMap.size} rules`);
    } catch (error) {
      console.error('Error loading rule statistics:', error);
    }
  };

  // Setup realtime subscription for trade history
  const setupRealtimeSubscription = async () => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) {
      console.log('📡 Skipping realtime subscription setup - no wallet address');
      return;
    }

    // Clean up existing subscription
    if (realtimeSubscriptionRef.current) {
      console.log('📡 Cleaning up existing realtime subscription');
      realtimeSubscriptionRef.current.unsubscribe();
      realtimeSubscriptionRef.current = null;
    }

    try {
      // Get user ID for filtering
      const user = await userService.getUserByWallet(effectiveWalletAddress);
      if (!user) {
        console.log('📡 No user found for wallet, skipping realtime subscription');
        return;
      }

      console.log(`📡 Setting up realtime subscription for user ${user.id} (wallet: ${effectiveWalletAddress.slice(0, 8)}...)`);

      // Setup realtime subscription to sniper_trade_history table
      const subscription = adminSupabase
        .channel(`sniper_trade_history_${user.id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'sniper_trade_history',
            filter: `user_id=eq.${user.id}`
          },
          (payload: any) => {
            console.log('📡 Realtime trade history update:', payload.eventType, payload.new || payload.old);
            
            if (payload.eventType === 'INSERT') {
              // Add new trade to history at the top (most recent first)
              setTradeHistory(prev => {
                // Check if this trade already exists (prevent duplicates)
                const exists = prev.some(trade => trade.id === payload.new.id);
                if (exists) {
                  console.log('📡 Trade already exists, skipping duplicate');
                  return prev;
                }
                
                console.log(`📡 Adding new trade to history: ${payload.new.token_symbol} - ${payload.new.status}`);
                return [payload.new, ...prev];
              });
              
              // Refresh statistics when new trade is added
              loadRuleStatistics();
              
              // Show a subtle indication that a new trade was added
              if (activeTab === 'transactions') {
                console.log('📡 New trade added to transactions view');
              }
            } else if (payload.eventType === 'UPDATE') {
              // Update existing trade
              setTradeHistory(prev => 
                prev.map(trade => {
                  if (trade.id === payload.new.id) {
                    console.log(`📡 Updating trade ${trade.id}: ${payload.new.status}`);
                    return { ...trade, ...payload.new };
                  }
                  return trade;
                })
              );
              
              // Refresh statistics when trade is updated
              loadRuleStatistics();
            } else if (payload.eventType === 'DELETE') {
              // Remove deleted trade
              setTradeHistory(prev => {
                const filtered = prev.filter(trade => trade.id !== payload.old.id);
                console.log(`📡 Removed trade ${payload.old.id} from history`);
                return filtered;
              });
              
              // Refresh statistics when trade is deleted
              loadRuleStatistics();
            }
          }
        )
        .subscribe((status) => {
          console.log('📡 Realtime subscription status:', status);
          if (status === 'SUBSCRIBED') {
            console.log('✅ Successfully subscribed to sniper trade history realtime updates');
          } else if (status === 'CLOSED') {
            console.log('❌ Realtime subscription closed');
          } else if (status === 'CHANNEL_ERROR') {
            console.error('❌ Realtime subscription error');
          }
        });

      realtimeSubscriptionRef.current = subscription;
      console.log('📡 Realtime subscription setup completed');
    } catch (error) {
      console.error('❌ Error setting up realtime subscription:', error);
    }
  };

  // Show sniper bot notifications
  function showSniperNotification(
    type: 'loading' | 'success' | 'error',
    botName: string,
    tokenSymbol: string,
    tokenName: string,
    amount: number,
    walletAddress: string,
    txSignature?: string,
    errorMessage?: string,
    matchedConditions?: LocalSniperCondition[]
  ) {
    const timestamp = new Date().toLocaleTimeString();
    const walletDisplay = `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`;
    
    // Create rule conditions summary
    const conditionsText = matchedConditions && matchedConditions.length > 0 
      ? ` (Matched: ${matchedConditions.map(c => formatCondition(c)).join(', ')})`
      : '';
    
    if (type === 'loading') {
      return showNotification(
        `Sniper Bot "${botName}" sniping ${tokenSymbol} for ${amount} SOL on wallet ${walletDisplay}${conditionsText}`,
        'loading'
      );
    } else if (type === 'success') {
      let message = `✅ Sniper Bot "${botName}" sniped ${tokenSymbol} for ${amount} SOL at ${timestamp} (Wallet: ${walletDisplay})${conditionsText}`;
      if (txSignature) {
        message += ` - TX: ${txSignature.slice(0, 8)}...${txSignature.slice(-8)}`;
      }
      return showNotification(message, 'success');
    } else if (type === 'error') {
      let message = `❌ Sniper Bot "${botName}" failed to snipe ${tokenSymbol} for ${amount} SOL at ${timestamp} (Wallet: ${walletDisplay})${conditionsText}`;
      if (errorMessage) {
        message += ` - Error: ${errorMessage}`;
      }
      return showNotification(message, 'error');
    }
  }

  // Initialize WebSocket connection but only start monitoring when bots are active
  useEffect(() => {
    const initializeConnection = async () => {
      setIsLoading(true);
      setConnectionStatus('connecting');
      
      try {
        // Ensure WebSocket connection
        const connected = await wsService.ensureConnection();
        
        if (connected) {
          setConnectionStatus('connected');
          console.log('🔗 Sniper Bot WebSocket connected - ready for monitoring when bots are active');
        } else {
          setConnectionStatus('error');
        }
      } catch (error) {
        console.error('Error initializing sniper bot connection:', error);
        setConnectionStatus('error');
      } finally {
        setIsLoading(false);
      }
    };

    initializeConnection();

    // Load trading wallets and rules
    loadTradingWallets();
    loadSniperRules();
    loadTradeHistory();
    loadRuleStatistics();

    // Cleanup function
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      // Don't leave "latest" room - might be used by token feeds
      // wsService.leaveRoom("latest");
      
      // Clean up all token subscriptions
      tokenSubscriptionsRef.current.forEach((unsubscribes, tokenAddress) => {
        unsubscribes.forEach(unsub => unsub());
        // Don't leave token rooms - they might be used by token feeds
        // wsService.leaveRoom(`transaction:${tokenAddress}`);
        // wsService.leaveRoom(`holders:${tokenAddress}`);
        // wsService.leaveRoom(`token:${tokenAddress}`);
      });
      tokenSubscriptionsRef.current.clear();
      masterTokenDataRef.current.clear();
      subscribedTokensRef.current = new Set();
    };
  }, [walletUser.walletAddress]);

  // Smart monitoring system - only start websockets and polling when bots are active
  useEffect(() => {
    if (!isSnipingActive || connectionStatus !== 'connected') {
      // Stop all monitoring when no active bots or not connected
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      // wsService.leaveRoom("latest");
      console.log('⏹️ SNIPER MONITORING: Stopped (no active bots or not connected)');
      return;
    }

    // Start monitoring when bots are active
    console.log('🎯 SNIPER MONITORING: Starting websockets and polling (active bots detected)');
    
    // Join the "latest" room to discover new tokens
    wsService.joinRoom("latest");
    
    // Set up listener for latest data
    const unsubscribe = wsService.on("latest", (data) => {
      // Extract token address/mint from the data
      if (Array.isArray(data)) {
        data.forEach(item => {
          const address = extractTokenAddress(item);
          if (address && typeof address === 'string') {
            const tokenInfo = extractTokenInfo(item);
            subscribeToTokenRooms(address, tokenInfo);
          }
        });
      } else {
        const address = extractTokenAddress(data);
        if (address && typeof address === 'string') {
          const tokenInfo = extractTokenInfo(data);
          subscribeToTokenRooms(address, tokenInfo);
        }
      }
    });
    
    // Store unsubscribe function
    unsubscribeRef.current = unsubscribe;

    // Cleanup when monitoring stops
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      // wsService.leaveRoom("latest");
      console.log('🔌 SNIPER MONITORING: Cleanup completed');
    };
  }, [isSnipingActive, connectionStatus]);

  // Setup realtime subscription whenever wallet changes (not just when transactions tab is selected)
  useEffect(() => {
    if (getEffectiveWalletAddress()) {
      setupRealtimeSubscription();
    }
    
    return () => {
      // Cleanup realtime subscription when wallet changes
      if (realtimeSubscriptionRef.current) {
        realtimeSubscriptionRef.current.unsubscribe();
        realtimeSubscriptionRef.current = null;
      }
    };
  }, [walletUser.walletAddress, walletUser.walletProvider]);

  // Load trade history when transactions tab is selected
  useEffect(() => {
    if (activeTab === 'transactions' && getEffectiveWalletAddress()) {
      loadTradeHistory();
      loadRuleStatistics();
    }
  }, [activeTab, walletUser.walletAddress, walletUser.walletProvider]);

  // Cleanup realtime subscription on unmount
  useEffect(() => {
    return () => {
      if (realtimeSubscriptionRef.current) {
        realtimeSubscriptionRef.current.unsubscribe();
        realtimeSubscriptionRef.current = null;
      }
    };
  }, []);

  // Keep refs in sync with state for WebSocket callbacks
  useEffect(() => {
    isSnipingActiveRef.current = isSnipingActive;
    console.log(`🔄 REF SYNC: Updated isSnipingActiveRef.current = ${isSnipingActive}`);
  }, [isSnipingActive]);

  // INDEPENDENT Auto-sell position monitoring - continues even when sniper bot is inactive
  useEffect(() => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) return;

    // Always start position monitoring when wallet is available
    // Auto-sell should work independently of sniper bot status
    if (!positionMonitoringCleanupRef.current) {
      positionMonitoringCleanupRef.current = sniperPositionsService.startPositionMonitoring(effectiveWalletAddress);
      console.log(`🤖 Started INDEPENDENT auto-sell position monitoring (continues even when sniper bot inactive)`);
    }

    // Only cleanup when wallet changes or component unmounts
    return () => {
      if (positionMonitoringCleanupRef.current) {
        positionMonitoringCleanupRef.current();
        positionMonitoringCleanupRef.current = null;
        console.log(`⏹️ Stopped auto-sell position monitoring (wallet changed/unmounted)`);
      }
    };
  }, [walletUser.walletAddress, walletUser.walletProvider]); // Only depends on wallet, not sniper bot status

  useEffect(() => {
    sniperRulesRef.current = sniperRules;
    const activeCount = sniperRules.filter(r => r.isActive).length;
    console.log(`🔄 REF SYNC: Updated sniperRulesRef.current with ${sniperRules.length} rules (${activeCount} active)`);
  }, [sniperRules]);

  // Token data polling system - only runs when bots are active
  useEffect(() => {
    if (!isSnipingActive || connectionStatus !== 'connected') {
      return; // Only poll when bots are active and WebSocket is connected
    }

    let pollingInterval: NodeJS.Timeout;

    const startPolling = () => {
      console.log('🔄 SNIPER POLLING: Starting token data polling (every 3 seconds) - active bots detected');
      
      pollingInterval = setInterval(async () => {
        const trackedTokens = Array.from(subscribedTokensRef.current);
        
        if (trackedTokens.length === 0) {
          return; // No tokens to poll
        }
        
        console.log(`📊 SNIPER POLLING: Polling ${trackedTokens.length} tracked tokens for active bots...`);
        
        // Poll tokens in batches to avoid overwhelming the API
        const batchSize = 5; // Poll 5 tokens at a time
        for (let i = 0; i < trackedTokens.length; i += batchSize) {
          const batch = trackedTokens.slice(i, i + batchSize);
          
          // Poll all tokens in this batch simultaneously
          await Promise.allSettled(
            batch.map(tokenAddress => pollTokenData(tokenAddress))
          );
          
          // Small delay between batches to be nice to the API
          if (i + batchSize < trackedTokens.length) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      }, 3000); // Poll every 3 seconds
    };

    // Start polling
    startPolling();

    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        console.log('🛑 SNIPER POLLING: Stopped token data polling (no active bots)');
      }
    };
  }, [isSnipingActive, connectionStatus]); // Start polling when bots are active and connected

  // Debug wallet state changes
  useEffect(() => {
    const effectiveWallet = getEffectiveWalletAddress();
    console.log('🔄 SNIPER BOT: Wallet state changed:', {
      effectiveWallet: effectiveWallet,
      directWallet: typeof window !== 'undefined' && window.solana?.publicKey ? window.solana.publicKey.toString() : null,
      hookWallet: walletUser.walletAddress,
      usingDirect: effectiveWallet === (typeof window !== 'undefined' && window.solana?.publicKey ? window.solana.publicKey.toString() : null),
      timestamp: new Date().toLocaleTimeString()
    });
  }, [walletUser.walletAddress, walletUser.walletProvider]);

  // ADDED: Quick balance refresh when wallet changes but trading wallets are already loaded
  useEffect(() => {
    const effectiveWallet = getEffectiveWalletAddress();
    
    // If we have trading wallets already loaded but wallet changed, just refresh balances
    if (effectiveWallet && tradingWallets.length > 0 && !isLoadingWallets) {
      console.log('🔄 Wallet changed with existing trading wallets - refreshing balances only');
      refreshTradingWalletBalances();
    }
  }, [walletUser.walletAddress, walletUser.walletProvider, tradingWallets.length, isLoadingWallets]);

  // Function to extract token address from various data formats
  const extractTokenAddress = (item: any): string | null => {
    if (!item) return null;
    
    // Handle nested token structure from "latest" room
    if (item.token && item.token.mint) {
      return item.token.mint;
    }
    
    // Fallback to other possible direct fields
    return item.mint || item.tokenAddress || item.address || null;
  };

  // Function to extract token information
  const extractTokenInfo = (item: any): { name: string; symbol: string } => {
    if (!item) return { name: '', symbol: '' };
    
    // Handle nested token structure from "latest" room
    if (item.token && item.token.name && item.token.symbol) {
      return { name: item.token.name, symbol: item.token.symbol };
    }
    
    // Fallback to other possible direct fields
    return { name: item.name || '', symbol: item.symbol || '' };
  };

  // Function to subscribe to individual token rooms (only when bots are active)
  const subscribeToTokenRooms = (tokenAddress: string, tokenInfo: { name: string; symbol: string }) => {
    if (!tokenAddress || subscribedTokensRef.current.has(tokenAddress)) {
      return; // Already subscribed to this token
    }

    // Only subscribe if bots are active
    if (!isSnipingActiveRef.current) {
      console.log(`⏭️ SNIPER MONITORING: Skipping token ${tokenInfo.symbol} (no active bots)`);
      return;
    }

    // Debug: Show when new tokens are discovered and monitored
    console.log(`🆕 SNIPER MONITORING: New token discovered and monitoring started: ${tokenInfo.name} (${tokenInfo.symbol}) - ${tokenAddress.slice(0, 8)}...`);

    // Increment total tokens tracked counter
    totalTokensTrackedRef.current += 1;
    
    // Update display value occasionally (every 5 tokens) to prevent constant re-renders
    if (totalTokensTrackedRef.current % 5 === 0) {
      setDisplayTotalTokensTracked(totalTokensTrackedRef.current);
    }

    // Performance check: Don't subscribe to more tokens if we're near limit
    if (masterTokenDataRef.current.size >= cleanupThreshold) {
      // Continue with subscription - cleanup will handle it in updateTokenTable
    }

    // Initialize token data with basic info
    const initialData = {
      name: tokenInfo.name,
      symbol: tokenInfo.symbol,
      address: tokenAddress,
      buys: 0,
      sells: 0,
      tokensBought: 0,
      discoveredAt: new Date().toLocaleTimeString()
    };
    
    // Store token metadata
    tokenMetadataRef.current.set(tokenAddress, tokenInfo);
    
    // Initialize in master data
    updateTokenTable(tokenAddress, initialData, `NEW TOKEN: ${tokenInfo.name} (${tokenInfo.symbol})`);
    
    // Add to subscribed tokens set
    subscribedTokensRef.current = new Set([...subscribedTokensRef.current, tokenAddress]);
    
    const unsubscribeFunctions: (() => void)[] = [];

    // 4. Transactions
    wsService.joinRoom(`transaction:${tokenAddress}`);
    const unsubTransaction = wsService.on(`transaction:${tokenAddress}`, (data) => {
      if (Array.isArray(data)) {
        data.forEach(tx => {
          // Get current data to update buy/sell counters
          const currentData = masterTokenDataRef.current.get(tokenAddress) || {};
          
          const txData = {
            lastTxType: tx.type?.toUpperCase(),
            lastTxAmount: tx.amount,
            lastTxVolume: tx.volume,
            priceUsd: tx.priceUsd,
            lastTxWallet: tx.wallet?.slice(0, 8) + '...',
            lastTxProgram: tx.program,
            lastTxTime: new Date().toLocaleTimeString(),
            // Increment buy or sell counter based on transaction type
            buys: tx.type?.toLowerCase() === 'buy' ? (currentData.buys || 0) + 1 : (currentData.buys || 0),
            sells: tx.type?.toLowerCase() === 'sell' ? (currentData.sells || 0) + 1 : (currentData.sells || 0),
            // Track tokens bought (only for buy transactions)
            tokensBought: tx.type?.toLowerCase() === 'buy' ? tx.amount : (currentData.tokensBought || 0)
          };
          updateTokenTable(tokenAddress, txData, `TRANSACTION: ${tx.type?.toUpperCase()}`);
        });
      }
    });
    unsubscribeFunctions.push(unsubTransaction);

    // 11. Holders update
    wsService.joinRoom(`holders:${tokenAddress}`);
    const unsubHolders = wsService.on(`holders:${tokenAddress}`, (data) => {
      if (data && data.total !== undefined) {
        const holdersData = {
          holders: data.total
        };
        updateTokenTable(tokenAddress, holdersData, 'HOLDERS UPDATE');
      }
    });
    unsubscribeFunctions.push(unsubHolders);

    // 12. Token Changes
    wsService.joinRoom(`token:${tokenAddress}`);
    const unsubToken = wsService.on(`token:${tokenAddress}`, (data) => {
      if (data && data.price && data.marketCap && data.liquidity) {
        const tokenData = {
          priceUsd: data.price.usd,
          marketCapUsd: data.marketCap.usd,
          liquidityUsd: data.liquidity.usd,
          lpBurn: data.lpBurn,
          market: data.market,
          tokenSupply: data.tokenSupply,
          curvePercentage: data.curvePercentage, // Fixed: using camelCase curvePercentage
          lastUpdated: new Date(data.lastUpdated).toLocaleTimeString()
        };
        updateTokenTable(tokenAddress, tokenData, 'TOKEN UPDATE');
      }
    });
    unsubscribeFunctions.push(unsubToken);

    // Store unsubscribe functions for cleanup
    tokenSubscriptionsRef.current.set(tokenAddress, unsubscribeFunctions);
  };

  // Monitor connection status changes - FIXED to prevent re-renders
  useEffect(() => {
    const checkConnectionStatus = () => {
      const status = wsService.getConnectionStatus();
      // Use ref to get current status to avoid dependency
      if (status !== connectionStatus) {
        setConnectionStatus(status);
      }
    };

    const interval = setInterval(checkConnectionStatus, 5000); // Reduced frequency from 1s to 5s
    return () => clearInterval(interval);
  }, []); // Removed connectionStatus dependency to prevent re-render loops

  // Calculate total SOL needed and validate balances
  const calculateTotalSolNeeded = () => {
    // If we're in creation/edit mode, use form values
    if (isCreatingBot) {
      const solPerSnipe = parseFloat(solAmountPerSnipe) || 0;
      const maxTokens = parseFloat(maxTokensToSnipe) || 0;
      const selectedWalletCount = selectedWallets.size;
      
      if (sniperStrategy === 'mirrored') {
        // Mirrored: all wallets buy same tokens, so total = sol_per_snipe × max_tokens × wallet_count
      return solPerSnipe * maxTokens * selectedWalletCount;
      } else if (sniperStrategy === 'individual') {
        // Individual: each wallet buys full amount independently, so total = sol_per_snipe × max_tokens × wallet_count  
        return solPerSnipe * maxTokens * selectedWalletCount;
      } else {
        // Diversified: wallets split the total tokens exactly, so total = sol_per_snipe × max_tokens (no multiplier)
        return solPerSnipe * maxTokens;
      }
    }
    
    // Otherwise, calculate based on all active rules with their actual configurations
    let totalNeeded = 0;
    sniperRules.filter(r => r.isActive).forEach(rule => {
      if (rule.solAmountPerSnipe && rule.maxTokensToSnipe && rule.tradingWalletAddresses) {
        const strategyMode = rule.configurationMetadata?.sniperStrategy || 
                          (rule.configurationMetadata?.enableSniperMirroring ? 'mirrored' : 'diversified');
        
        if (strategyMode === 'mirrored') {
          // Mirrored: all wallets buy same tokens
        totalNeeded += rule.solAmountPerSnipe * rule.maxTokensToSnipe * rule.tradingWalletAddresses.length;
        } else {
          // Diversified: wallets split tokens (no surplus)
          totalNeeded += rule.solAmountPerSnipe * rule.maxTokensToSnipe;
        }
      }
    });
    return totalNeeded;
  };

  const getTotalAvailableBalance = () => {
    let total = 0;
    selectedWallets.forEach(walletAddress => {
      total += walletBalances.get(walletAddress) || 0;
    });
    return total;
  };

  const hasInsufficientBalance = () => {
    const totalNeeded = calculateTotalSolNeeded();
    const totalAvailable = getTotalAvailableBalance();
    return totalNeeded > totalAvailable;
  };

  // Get detailed balance validation results for each wallet
  const getWalletValidationResults = () => {
    if (selectedWallets.size === 0) return { isValid: false, issues: ['No wallets selected'] };

    const solPerSnipe = parseFloat(solAmountPerSnipe) || 0;
    const maxTokens = parseFloat(maxTokensToSnipe) || 0;
    
    // Debug logging
    console.log('🔍 VALIDATION DEBUG:', {
      solAmountPerSnipe: solAmountPerSnipe,
      maxTokensToSnipe: maxTokensToSnipe,
      solPerSnipe: solPerSnipe,
      maxTokens: maxTokens,
      selectedWalletsSize: selectedWallets.size,
      enableSniperMirroring: sniperStrategy === 'mirrored'
    });
    
    if (solPerSnipe <= 0 || maxTokens <= 0) {
      console.log('❌ Invalid inputs:', { solPerSnipe, maxTokens });
      return { isValid: false, issues: ['Invalid SOL amount or max tokens'] };
    }

    const issues: string[] = [];
    let isValid = true;

    // Check each wallet individually
    selectedWallets.forEach(walletAddress => {
      const wallet = tradingWallets.find(w => w.public_key === walletAddress);
      const balance = walletBalances.get(walletAddress) || 0;
      
      console.log(`🔍 Checking wallet ${walletAddress.slice(0, 8)}...`, {
        walletName: wallet?.name,
        balance: balance,
        balanceRaw: walletBalances.get(walletAddress)
      });
      
      if (!wallet) {
        issues.push(`Wallet ${walletAddress.slice(0, 6)}... not found`);
        isValid = false;
        return;
      }

      // Calculate required balance per wallet based on strategy
      let requiredBalance: number;
      if (sniperStrategy === 'mirrored') {
        // Mirrored mode: Each wallet needs enough for ALL trades
        requiredBalance = solPerSnipe * maxTokens;
        console.log(`🪞 Mirrored mode: ${solPerSnipe} × ${maxTokens} = ${requiredBalance}`);
      } else if (sniperStrategy === 'individual') {
        // Individual mode: Each wallet buys the full amount independently
        requiredBalance = solPerSnipe * maxTokens;
        console.log(`👤 Individual mode: ${solPerSnipe} × ${maxTokens} = ${requiredBalance}`);
      } else {
        // Diversified mode: Each wallet gets exact share of total tokens (no surplus)
        const baseTokensPerWallet = Math.floor(maxTokens / selectedWallets.size);
        const remainingTokens = maxTokens % selectedWallets.size;
        const walletIndex = Array.from(selectedWallets).indexOf(walletAddress);
        const tokensForThisWallet = baseTokensPerWallet + (walletIndex < remainingTokens ? 1 : 0);
        requiredBalance = solPerSnipe * tokensForThisWallet;
        console.log(`🎯 Diversified mode: wallet ${walletIndex + 1} gets ${tokensForThisWallet} tokens (${baseTokensPerWallet} base + ${walletIndex < remainingTokens ? 1 : 0} extra) = ${requiredBalance} SOL needed`);
      }

      console.log(`💰 Balance check: ${balance} < ${requiredBalance} = ${balance < requiredBalance}`);

      if (balance < requiredBalance) {
        const strategy = sniperStrategy === 'mirrored' ? 'mirrored' : sniperStrategy === 'individual' ? 'individual' : 'diversified';
        let tokensForThisWallet: number;
        
        if (sniperStrategy === 'mirrored' || sniperStrategy === 'individual') {
          tokensForThisWallet = maxTokens;
        } else {
          // Diversified: calculate exact tokens for this wallet
          const baseTokensPerWallet = Math.floor(maxTokens / selectedWallets.size);
          const remainingTokens = maxTokens % selectedWallets.size;
          const walletIndex = Array.from(selectedWallets).indexOf(walletAddress);
          tokensForThisWallet = baseTokensPerWallet + (walletIndex < remainingTokens ? 1 : 0);
        }
        
        const issueText = `${wallet.name}: has ${balance.toFixed(3)} SOL, needs ${requiredBalance.toFixed(3)} SOL for ${tokensForThisWallet} tokens (${strategy} mode)`;
        issues.push(issueText);
        console.log(`❌ VALIDATION FAILED: ${issueText}`);
        isValid = false;
      } else {
        console.log(`✅ Wallet ${wallet.name} has sufficient balance`);
      }
    });

    console.log('🔍 VALIDATION RESULT:', { isValid, issues });
    return { isValid, issues };
  };

  // Check if current configuration is valid for bot activation
  const hasInsufficientBalanceDetailed = () => {
    const validation = getWalletValidationResults();
    return !validation.isValid;
  };

  // Toggle wallet selection
  const toggleWalletSelection = (walletAddress: string) => {
    const newSelection = new Set(selectedWallets);
    if (newSelection.has(walletAddress)) {
      newSelection.delete(walletAddress);
    } else {
      newSelection.add(walletAddress);
    }
    setSelectedWallets(newSelection);
  };

  // Render wallet selection section
  const renderWalletSelection = () => (
    <div className="mb-1.5">
      <h4 className="text-[#24c35b] text-xs font-medium mb-1">Trading Wallets</h4>
      {isLoadingWallets ? (
        <div className="flex items-center justify-center py-2">
          <Loader size={12} className="text-[#24c35b] animate-spin mr-1" />
          <span className="text-gray-400 text-xs">Loading...</span>
        </div>
      ) : tradingWallets.length === 0 ? (
        <div className="text-center py-2">
          <p className="text-gray-400 text-xs">No wallets found</p>
        </div>
      ) : (
        <div className="space-y-1 max-h-28 overflow-y-auto">
          {tradingWallets.map(wallet => {
            const balance = walletBalances.get(wallet.public_key) || 0;
            const isSelected = selectedWallets.has(wallet.public_key);
            
            return (
              <div
                key={wallet.public_key}
                className={`p-1 rounded border cursor-pointer transition-all ${
                  isSelected 
                    ? 'border-[#24c35b] bg-[#24c35b]/10' 
                    : 'border-gray-200 bg-white hover:border-[#24c35b]/40'
                }`}
                onClick={() => toggleWalletSelection(wallet.public_key)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded border mr-1.5 flex items-center justify-center ${
                      isSelected ? 'border-[#24c35b] bg-[#24c35b]' : 'border-gray-400'
                    }`}>
                      {isSelected && (
                        <div className="w-1.5 h-1.5 bg-white rounded-sm"></div>
                      )}
                    </div>
                    <div>
                      <p className="text-black text-xs font-medium">{wallet.name}</p>
                      <p className="text-gray-600 text-xs font-mono">
                        {wallet.public_key.slice(0, 6)}...{wallet.public_key.slice(-4)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-mono text-black">
                      {balance.toFixed(3)} SOL
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Render rules creation step
  const renderRulesCreation = () => (
    <div className="space-y-1.5">
      <div className="text-center mb-1.5">
        <h3 className="text-black text-base font-semibold mb-0.5">
          {editingBotId ? 'Edit Sniper Bot' : 'Create New Sniper Bot'}
        </h3>
        <p className="text-gray-500 text-xs">
          {editingBotId ? 'Modify rules for your bot' : 'Set up rules for automatic token purchases'}
        </p>
      </div>

      {/* Bot Name */}
      <div>
        <label className="text-black text-xs font-medium mb-1 block">
          Bot Name
        </label>
        <input
          type="text"
          value={newBotName}
          onChange={(e) => setNewBotName(e.target.value)}
          className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-black text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40"
          placeholder="e.g., Low Cap Hunter"
        />
      </div>

      {/* Rules Configuration */}
      <div>
        <label className="text-black text-xs font-medium mb-1 block">
          Sniper Rules <span className="text-gray-500 font-normal">(ALL conditions must be true)</span>
        </label>
        <div className="space-y-1">
          {newBotRules.map((rule, index) => (
            <div key={index}>
              <div className="p-1 rounded border border-gray-200 bg-white">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-black text-xs font-medium">
                    Condition {index + 1}
                  </span>
                  <button
                    onClick={() => {
                      const updatedRules = newBotRules.filter((_, i) => i !== index);
                      setNewBotRules(updatedRules);
                    }}
                    className="text-gray-500 hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={10} />
                  </button>
                </div>
              
                <div className="space-y-1">
                  <div className="grid grid-cols-3 gap-1">
                    <GlobalDropdown
                      options={Object.entries(SNIPER_FIELD_LABELS).map(([value, label]) => ({
                        value,
                        label
                      }))}
                      value={rule.field}
                      onChange={(value) => {
                        const updatedRules = [...newBotRules];
                        const currentRule = rule;
                        
                        // Auto-set operator to '=' for name and symbol fields
                        if (value === 'name' || value === 'symbol') {
                          updatedRules[index] = { 
                            ...currentRule, 
                            field: value as LocalSniperCondition['field'],
                            operator: '='
                          };
                        } else {
                          updatedRules[index] = { ...currentRule, field: value as LocalSniperCondition['field'] };
                        }
                        
                        setNewBotRules(updatedRules);
                      }}
                      placeholder="Field"
                    />
                    
                    <GlobalDropdown
                      options={[
                        { value: '>', label: '>', description: 'greater than' },
                        { value: '<', label: '<', description: 'less than' },
                        { value: '>=', label: '>=', description: 'greater/equal' },
                        { value: '<=', label: '<=', description: 'less/equal' },
                        { value: '=', label: '=', description: rule.field === 'name' || rule.field === 'symbol' ? 'contains text' : 'equals exactly' },
                        { value: 'increase%', label: '+%', description: 'increased by %' },
                        { value: 'decrease%', label: '-%', description: 'decreased by %' }
                      ].filter(option => {
                        // Only show '=' operator for name and symbol fields
                        if ((rule.field === 'name' || rule.field === 'symbol') && option.value !== '=') {
                          return false;
                        }
                        // Hide percentage operators for certain fields (existing logic)
                        if ((option.value === 'increase%' || option.value === 'decrease%') && 
                            !['marketCap', 'liquidity', 'holders', 'txVolume'].includes(rule.field)) {
                          return false;
                        }
                        return true;
                      })}
                      value={rule.operator}
                      onChange={(value) => {
                        const updatedRules = [...newBotRules];
                        updatedRules[index] = { ...rule, operator: value as LocalSniperCondition['operator'] };
                        setNewBotRules(updatedRules);
                      }}
                      placeholder="Op"
                    />
                    
                    {(rule.field === 'name' || rule.field === 'symbol') ? (
                      <input
                        type="text"
                        value={String(rule.value || '')}
                        onChange={(e) => {
                          const updatedRules = [...newBotRules];
                          updatedRules[index] = { ...rule, value: e.target.value as any };
                          setNewBotRules(updatedRules);
                        }}
                        className="bg-white border border-gray-200 rounded px-1.5 py-1 text-black text-xs hover:border-[#24c35b]/40 focus:border-[#24c35b]/50"
                        placeholder="Token name or symbol"
                      />
                    ) : (
                      <input
                        type="text"
                        value={rule.value}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            const updatedRules = [...newBotRules];
                            updatedRules[index] = { ...rule, value: parseFloat(value) || 0 };
                            setNewBotRules(updatedRules);
                          }
                        }}
                        className="bg-white border border-gray-200 rounded px-1.5 py-1 text-black text-xs hover:border-[#24c35b]/40 focus:border-[#24c35b]/50"
                        placeholder="Value"
                        inputMode="decimal"
                      />
                    )}
                  </div>
                </div>
              </div>
              
              {/* AND Logic Indicator */}
              {index < newBotRules.length - 1 && (
                <div className="flex justify-center py-1">
                  <span className="text-[#24c35b] text-xs font-bold bg-[#24c35b]/10 border border-[#24c35b]/30 px-2 py-0.5 rounded">
                    AND
                  </span>
                </div>
              )}
            </div>
          ))}
          
          <button
            onClick={() => {
              setNewBotRules([...newBotRules, {
                field: 'marketCap',
                operator: '>',
                value: 100000
              }]);
            }}
            className="w-full p-1.5 border border-dashed border-gray-600/50 rounded text-gray-400 hover:text-white hover:border-[#24c35b]/50 transition-all text-xs"
          >
            <Plus size={10} className="mr-1 inline" />
            Add Rule
          </button>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-2">
        <button
          onClick={() => {
            setIsCreatingBot(false);
            setNewBotName('');
            setNewBotRules([]);
            setEditingBotId(null);
            setSniperStrategy('diversified');
          }}
          className="px-3 py-1.5 rounded border border-gray-600/50 text-gray-400 hover:text-white hover:border-gray-500 transition-all text-xs"
        >
          Cancel
        </button>
        <button
          onClick={() => setCreationStep('config')}
          disabled={!newBotName.trim() || newBotRules.length === 0}
          className="px-3 py-1.5 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] hover:bg-[#24c35b]/30 transition-all text-xs disabled:opacity-50"
        >
          Next: Configure
        </button>
      </div>
    </div>
  );

  // Render config creation step
  const renderConfigCreation = () => (
    <div className="space-y-1.5">
      <div className="text-center mb-1.5">
        <h3 className="text-black text-base font-semibold mb-0.5">
          {editingBotId ? `Update Bot: ${newBotName}` : `Configure Bot: ${newBotName}`}
        </h3>
        <p className="text-gray-500 text-xs">
          {editingBotId ? 'Update wallet and amount settings' : 'Select wallets and amounts'}
        </p>
      </div>

      {/* Reuse existing configuration components */}
      {renderWalletSelection()}
      
      {/* Trading Configuration in Grid */}
      <div className="grid grid-cols-3 gap-2 mb-1.5">
        <div>
          <label className="text-black text-xs font-medium mb-1 block">
            SOL per Snipe
          </label>
          <input
            type="text"
            value={solAmountPerSnipe}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                setSolAmountPerSnipe(value);
              }
            }}
            className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-black text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40"
            placeholder="0.1"
            inputMode="decimal"
          />
        </div>

        <div>
          <label className="text-black text-xs font-medium mb-1 block">
            Tokens to Snipe
          </label>
          <input
            type="text"
            value={maxTokensToSnipe}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d+$/.test(value)) {
                setMaxTokensToSnipe(value);
              }
            }}
            className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-black text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40"
            placeholder="5"
            inputMode="numeric"
          />
        </div>

        <div>
          <label className="text-black text-xs font-medium mb-1 block">
            Slippage (%)
          </label>
          <input
            type="text"
            value={slippagePercent}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                setSlippagePercent(value);
              }
            }}
            className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-black text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40"
            placeholder="35"
            inputMode="decimal"
          />
        </div>
      </div>

      {/* Wallet Strategy Configuration - Three Options */}
      <div className="mb-1.5">
        <label className="text-black text-xs font-medium mb-1 block">
          Wallet Strategy
        </label>
        <div className="grid grid-cols-3 gap-1">
          {/* Individual */}
          <button
            onClick={() => setSniperStrategy('individual')}
            className={`p-1.5 rounded border text-xs font-medium transition-all ${
              sniperStrategy === 'individual'
                ? 'border-[#24c35b] bg-[#24c35b]/20 text-[#24c35b]'
                : 'border-gray-600/20 bg-gray-600/5 text-gray-400 hover:border-[#24c35b]/40 hover:text-white'
          }`}
        >
            <div className="text-center">
              <div className="font-medium">Individual</div>
              <div className="text-[10px] mt-0.5 opacity-80">Independent</div>
              </div>
          </button>

          {/* Diversified */}
          <button
            onClick={() => selectedWallets.size > 1 && setSniperStrategy('diversified')}
            disabled={selectedWallets.size <= 1}
            className={`p-1.5 rounded border text-xs font-medium transition-all ${
              sniperStrategy === 'diversified'
                ? 'border-[#24c35b] bg-[#24c35b]/20 text-[#24c35b]'
                : selectedWallets.size <= 1
                ? 'border-gray-600/20 bg-gray-600/5 text-gray-600 cursor-not-allowed'
                : 'border-gray-600/20 bg-gray-600/5 text-gray-400 hover:border-[#24c35b]/40 hover:text-white'
            }`}
          >
            <div className="text-center">
              <div className="font-medium">Diversified</div>
              <div className="text-[10px] mt-0.5 opacity-80">
                {selectedWallets.size <= 1 ? 'Multiple wallets only' : 'Split tokens'}
              </div>
            </div>
          </button>

          {/* Mirrored */}
          <button
            onClick={() => selectedWallets.size > 1 && setSniperStrategy('mirrored')}
            disabled={selectedWallets.size <= 1}
            className={`p-1.5 rounded border text-xs font-medium transition-all ${
              sniperStrategy === 'mirrored'
                ? 'border-[#24c35b] bg-[#24c35b]/20 text-[#24c35b]'
                : selectedWallets.size <= 1
                ? 'border-gray-600/20 bg-gray-600/5 text-gray-600 cursor-not-allowed'
                : 'border-gray-600/20 bg-gray-600/5 text-gray-400 hover:border-[#24c35b]/40 hover:text-white'
            }`}
          >
            <div className="text-center">
              <div className="font-medium">Mirrored</div>
              <div className="text-[10px] mt-0.5 opacity-80">
                {selectedWallets.size <= 1 ? 'Multiple wallets only' : 'Same tokens'}
          </div>
        </div>
          </button>
      </div>

        {/* Strategy explanation */}
        <div className="mt-1 p-1 rounded bg-gray-600/5 border border-gray-600/20 text-[10px] text-gray-400">
          {sniperStrategy === 'mirrored' ? (
            <>
              <span className="text-blue-400 font-medium">Mirrored:</span> All {selectedWallets.size} wallets buy the same {parseFloat(maxTokensToSnipe) || 0} tokens
            </>
          ) : sniperStrategy === 'individual' ? (
            <>
              <span className="text-purple-400 font-medium">Individual:</span> Each wallet buys {parseFloat(maxTokensToSnipe) || 0} tokens independently. Never snipes the same token twice.
            </>
          ) : (
            <>
              <span className="text-green-400 font-medium">Diversified:</span> {selectedWallets.size} wallets split {parseFloat(maxTokensToSnipe) || 0} tokens exactly (no surplus)
            </>
          )}
        </div>
      </div>

      {/* Balance Summary - Enhanced */}
      {selectedWallets.size > 0 && (
        <div className="p-1.5 rounded border border-gray-600/20 bg-gray-600/5">
          <h5 className="text-white text-xs font-medium mb-1">Summary</h5>
          
          {/* Basic Configuration */}
          <div className="grid grid-cols-2 gap-1 text-xs mb-2">
            <div className="flex justify-between">
              <span className="text-gray-500">SOL per snipe:</span>
              <span className="text-white font-mono">{parseFloat(solAmountPerSnipe) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Total tokens:</span>
              <span className="text-white font-mono">{parseFloat(maxTokensToSnipe) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Wallets:</span>
              <span className="text-white font-mono">{selectedWallets.size}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Strategy:</span>
              <span className="text-white font-mono">
                {sniperStrategy === 'mirrored' ? 'Mirrored' : sniperStrategy === 'individual' ? 'Individual' : 'Diversified'}
              </span>
            </div>
          </div>

          {/* Budget Calculations */}
          <div className="grid grid-cols-2 gap-1 text-xs mb-2 pb-1 border-b border-gray-600/30">
            <div className="flex justify-between">
              <span className="text-gray-500">Total needed:</span>
              <span className="text-white font-mono font-bold">{calculateTotalSolNeeded().toFixed(3)} SOL</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Available:</span>
              <span className={`font-mono font-bold ${getTotalAvailableBalance() >= calculateTotalSolNeeded() ? 'text-[#24c35b]' : 'text-red-400'}`}>
                {getTotalAvailableBalance().toFixed(3)} SOL
              </span>
            </div>
          </div>

          {/* Strategy explanation */}
          <div className="mb-2 p-1 rounded bg-gray-600/5 border border-gray-600/20 text-[10px] text-gray-400">
            {sniperStrategy === 'mirrored' ? (
              <>
                <span className="text-blue-400 font-medium">Mirrored:</span> All {selectedWallets.size} wallets buy the same {parseFloat(maxTokensToSnipe) || 0} tokens
              </>
            ) : sniperStrategy === 'individual' ? (
              <>
                <span className="text-purple-400 font-medium">Individual:</span> Each of {selectedWallets.size} wallets buys {parseFloat(maxTokensToSnipe) || 0} tokens independently. Never snipes the same token twice.
              </>
            ) : (
              <>
                <span className="text-green-400 font-medium">Diversified:</span> {selectedWallets.size} wallets split {parseFloat(maxTokensToSnipe) || 0} tokens exactly (no surplus)
              </>
            )}
          </div>

          {/* Per-wallet breakdown */}
          <div className="space-y-1 mb-2">
            <div className="text-[10px] text-gray-500 font-medium">Per-Wallet Requirements:</div>
            {Array.from(selectedWallets).slice(0, 3).map(walletAddress => {
              const wallet = tradingWallets.find(w => w.public_key === walletAddress);
              const balance = walletBalances.get(walletAddress) || 0;
              const solPerSnipe = parseFloat(solAmountPerSnipe) || 0;
              const maxTokens = parseFloat(maxTokensToSnipe) || 0;
              
              let requiredPerWallet: number;
              let tokensPerWallet: number;
              
              if (sniperStrategy === 'mirrored') {
                // Mirrored: each wallet needs full amount
                requiredPerWallet = solPerSnipe * maxTokens;
                tokensPerWallet = maxTokens;
              } else if (sniperStrategy === 'individual') {
                // Individual: each wallet buys the full amount independently
                requiredPerWallet = solPerSnipe * maxTokens;
                tokensPerWallet = maxTokens;
              } else {
                // Diversified: each wallet gets exact share of total tokens (no surplus)
                const baseTokensPerWallet = Math.floor(maxTokens / selectedWallets.size);
                const remainingTokens = maxTokens % selectedWallets.size;
                const walletIndex = Array.from(selectedWallets).indexOf(walletAddress);
                tokensPerWallet = baseTokensPerWallet + (walletIndex < remainingTokens ? 1 : 0);
                requiredPerWallet = solPerSnipe * tokensPerWallet;
                console.log(`🎯 Diversified mode: wallet ${walletIndex + 1} gets ${tokensPerWallet} tokens (${baseTokensPerWallet} base + ${walletIndex < remainingTokens ? 1 : 0} extra) = ${requiredPerWallet} SOL needed`);
              }
              
              const hasEnough = balance >= requiredPerWallet;
              
              return (
                <div key={walletAddress} className="p-1 rounded bg-gray-600/5 border border-gray-600/20">
                  <div className="flex items-center justify-between text-[10px]">
                    <div className="flex-1">
                      <div className="text-white font-medium">{wallet?.name || 'Unknown'}</div>
                      <div className="text-gray-500 font-mono">
                        {walletAddress.slice(0, 6)}...{walletAddress.slice(-3)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-mono ${hasEnough ? 'text-[#24c35b]' : 'text-red-400'}`}>
                        {balance.toFixed(3)}
                      </div>
                      <div className="text-gray-500">
                        / {requiredPerWallet.toFixed(3)} needed
                      </div>
                    </div>
                    <div className="ml-2 text-right">
                      <div className="text-white font-mono text-[9px]">
                        {tokensPerWallet} tokens
                      </div>
                      <div className={`text-[9px] ${hasEnough ? 'text-[#24c35b]' : 'text-red-400'}`}>
                        {hasEnough ? '✓' : '✗'}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {selectedWallets.size > 3 && (
              <div className="text-[10px] text-gray-500 text-center">
                +{selectedWallets.size - 3} more wallets
              </div>
            )}
          </div>

          {/* Validation Status */}
          <div className="border-t border-gray-700/40 pt-1">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-500">Status:</span>
              <span className={`font-medium ${
                getWalletValidationResults().isValid ? 'text-[#24c35b]' : 'text-red-400'
              }`}>
                {getWalletValidationResults().isValid ? '✅ All Wallets Ready' : '❌ Issues Found'}
              </span>
            </div>
            
            {/* Budget Status */}
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-500">Budget:</span>
              <span className={`font-medium ${
                getTotalAvailableBalance() >= calculateTotalSolNeeded() ? 'text-[#24c35b]' : 'text-red-400'
              }`}>
                {getTotalAvailableBalance() >= calculateTotalSolNeeded() ? 
                  `✅ ${(getTotalAvailableBalance() - calculateTotalSolNeeded()).toFixed(3)} SOL remaining` : 
                  `❌ ${(calculateTotalSolNeeded() - getTotalAvailableBalance()).toFixed(3)} SOL short`
                }
              </span>
          </div>
          
            {!getWalletValidationResults().isValid && (
              <div className="mt-1 space-y-1">
                {getWalletValidationResults().issues.slice(0, 2).map((issue, index) => (
                  <div key={index} className="p-1 rounded bg-red-500/10 border border-red-500/20">
                    <div className="flex items-start">
                      <AlertCircle size={10} className="text-red-400 mr-1 mt-0.5 flex-shrink-0" />
                      <span className="text-red-400 text-[10px] leading-tight">
                        {issue}
                </span>
              </div>
            </div>
                ))}
                {getWalletValidationResults().issues.length > 2 && (
                  <div className="text-red-400 text-[10px] text-center">
                    +{getWalletValidationResults().issues.length - 2} more issue{getWalletValidationResults().issues.length - 2 !== 1 ? 's' : ''}
            </div>
          )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Auto-Sell Configuration */}
      <div className="p-1.5 rounded border border-gray-600/20 bg-gray-600/5">
        <div className="flex items-center justify-between mb-1">
          <h5 className="text-white text-xs font-medium">Auto-Sell Settings</h5>
          <button
            onClick={() => setEnableAutoSell(!enableAutoSell)}
            className={`text-xs px-2 py-0.5 rounded border transition-all ${
              enableAutoSell
                ? 'border-[#24c35b] bg-[#24c35b]/20 text-[#24c35b]'
                : 'border-gray-600/50 bg-gray-600/10 text-gray-400'
            }`}
          >
            {enableAutoSell ? 'ON' : 'OFF'}
          </button>
        </div>
        
        {enableAutoSell && (
          <>
            <p className="text-gray-500 text-[10px] mb-2">
              Automatically sell sniped tokens when profit/loss targets are reached
            </p>
            
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-black text-xs font-medium mb-1 block">
                  Profit % to Sell
                </label>
                <input
                  type="text"
                  value={autoSellProfitPercent}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setAutoSellProfitPercent(value);
                    }
                  }}
                  className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-black text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40"
                  placeholder="50"
                  inputMode="decimal"
                />
              </div>

              <div>
                <label className="text-black text-xs font-medium mb-1 block">
                  Loss % to Sell
                </label>
                <input
                  type="text"
                  value={autoSellLossPercent}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setAutoSellLossPercent(value);
                    }
                  }}
                  className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-black text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40"
                  placeholder="25"
                  inputMode="decimal"
                />
              </div>

              <div>
                <label className="text-black text-xs font-medium mb-1 block">
                  Position % to Sell
                </label>
                <input
                  type="text"
                  value={autoSellPositionPercent}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setAutoSellPositionPercent(value);
                    }
                  }}
                  className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-black text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40"
                  placeholder="100"
                  inputMode="decimal"
                />
              </div>
            </div>
            
            <div className="mt-2 p-1 rounded bg-blue-500/10 border border-blue-500/20 text-[10px] text-blue-400">
              <span className="font-medium">Auto-Sell:</span> When any sniped token reaches +{autoSellProfitPercent || 50}% profit or -{autoSellLossPercent || 25}% loss, {autoSellPositionPercent || 100}% of the position will be automatically sold.
            </div>
          </>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-2">
        <button
          onClick={() => setCreationStep('rules')}
          className="px-3 py-1.5 rounded border border-gray-600/50 text-gray-400 hover:text-white hover:border-gray-500 transition-all text-xs"
        >
          Back: Rules
        </button>
        <button
          onClick={handleFinishSetup}
          disabled={selectedWallets.size === 0 || hasInsufficientBalanceDetailed()}
          className="px-3 py-1.5 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] hover:bg-[#24c35b]/30 transition-all text-xs disabled:opacity-50"
        >
          {editingBotId ? 'Update Bot' : 'Finish Setup'}
        </button>
      </div>
    </div>
  );

  // Handle editing an existing bot
  const handleEditBot = (rule: LocalSniperRule) => {
    // Validate that the rule has complete configuration
    if (!rule.solAmountPerSnipe || !rule.maxTokensToSnipe || !rule.slippagePercent) {
      console.error(`🚫 Cannot edit bot "${rule.name}": Missing required configuration`);
      console.error(`Missing:`, {
        solAmountPerSnipe: !rule.solAmountPerSnipe,
        maxTokensToSnipe: !rule.maxTokensToSnipe, 
        slippagePercent: !rule.slippagePercent
      });
      // TODO: Show error toast to user
      return;
    }

    // Pre-populate the form with existing bot data
    setNewBotName(rule.name);
    setNewBotRules([...rule.conditions]);
    setEditingBotId(rule.id);
    
    // Load saved configuration from the rule (no fallbacks!)
    setSolAmountPerSnipe(rule.solAmountPerSnipe.toString());
    setMaxTokensToSnipe(rule.maxTokensToSnipe.toString());
    setSlippagePercent(rule.slippagePercent.toString());
    
    // Load saved trading wallets if available
    if (rule.tradingWalletAddresses && rule.tradingWalletAddresses.length > 0) {
      setSelectedWallets(new Set(rule.tradingWalletAddresses));
    } else {
      // Clear selected wallets if none saved
      setSelectedWallets(new Set());
    }
    
    // Load strategy setting from configurationMetadata
    const strategyFromMetadata = rule.configurationMetadata?.sniperStrategy || 
                                 (rule.configurationMetadata?.enableSniperMirroring ? 'mirrored' : 'diversified');
    setSniperStrategy(strategyFromMetadata);

    // Load auto-sell configuration
    setEnableAutoSell(rule.configurationMetadata?.enableAutoSell || false);
    setAutoSellProfitPercent((rule.configurationMetadata?.autoSellProfitPercent || 50).toString());
    setAutoSellLossPercent((rule.configurationMetadata?.autoSellLossPercent || 25).toString());
    setAutoSellPositionPercent((rule.configurationMetadata?.autoSellPositionPercent || 100).toString());
    
    // Enter creation flow in edit mode
    setIsCreatingBot(true);
    setCreationStep('rules');
    
    console.log(`📝 Editing sniper bot: ${rule.name} (${rule.id}) - loaded saved config:`, {
      solAmount: rule.solAmountPerSnipe,
      maxTokens: rule.maxTokensToSnipe,
      slippage: rule.slippagePercent,
      wallets: rule.tradingWalletAddresses?.length || 0
    });
  };

  // Handle finishing bot setup (create or update)
  const handleFinishSetup = async () => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress || !newBotName.trim() || newBotRules.length === 0) {
      return;
    }

    try {
      // Convert local rules format to service format
      const serviceConditions: Omit<SniperCondition, 'id' | 'rule_id' | 'created_at'>[] = newBotRules.map(rule => ({
        field: rule.field === 'tokensBought' ? 'txAmount' : rule.field,
        operator: rule.operator,
        value: rule.value
      }));

      if (editingBotId) {
        // Update existing bot
        await sniperRulesService.updateSniperRule(
          effectiveWalletAddress,
          editingBotId,
          { 
            name: newBotName, 
            conditions: serviceConditions, 
            solAmountPerSnipe: parseFloat(solAmountPerSnipe), 
            maxTokensToSnipe: parseInt(maxTokensToSnipe), 
            slippagePercent: parseFloat(slippagePercent), 
            tradingWalletAddresses: Array.from(selectedWallets), 
            totalSolBudget: calculateTotalSolNeeded(), 
            configurationMetadata: { 
              slippage: parseFloat(slippagePercent), 
              sniperStrategy: sniperStrategy, 
              enableSniperMirroring: sniperStrategy === 'mirrored', 
              createdAt: new Date().toISOString(), 
              lastUpdated: new Date().toISOString(),
              // Auto-sell configuration
              enableAutoSell: enableAutoSell,
              autoSellProfitPercent: parseFloat(autoSellProfitPercent) || 50,
              autoSellLossPercent: parseFloat(autoSellLossPercent) || 25,
              autoSellPositionPercent: parseFloat(autoSellPositionPercent) || 100
            } 
          }
        );
        console.log(`✅ Updated sniper bot: ${newBotName} with ${newBotRules.length} rule(s) and slippage ${slippagePercent}%`);
      } else {
        // Create new bot/rule in the database (inactive by default)
        await sniperRulesService.createSniperRule(
          effectiveWalletAddress,
          newBotName,
          serviceConditions,
          false, // Start in inactive/stopped state
          { 
            solAmountPerSnipe: parseFloat(solAmountPerSnipe), 
            maxTokensToSnipe: parseInt(maxTokensToSnipe), 
            slippagePercent: parseFloat(slippagePercent), 
            tradingWalletAddresses: Array.from(selectedWallets), 
            totalSolBudget: calculateTotalSolNeeded(), 
            configurationMetadata: { 
              slippage: parseFloat(slippagePercent), 
              sniperStrategy: sniperStrategy, 
              enableSniperMirroring: sniperStrategy === 'mirrored', 
              createdAt: new Date().toISOString(),
              // Auto-sell configuration
              enableAutoSell: enableAutoSell,
              autoSellProfitPercent: parseFloat(autoSellProfitPercent) || 50,
              autoSellLossPercent: parseFloat(autoSellLossPercent) || 25,
              autoSellPositionPercent: parseFloat(autoSellPositionPercent) || 100
            } 
          }
        );
        console.log(`✅ Created new sniper bot: ${newBotName} with ${newBotRules.length} rule(s) and slippage ${slippagePercent}% (inactive by default)`);
      }

      // Reload rules and statistics to show the changes
      await loadSniperRules();
      await loadRuleStatistics();

      // Reset creation state and go back to monitor
      setIsCreatingBot(false);
      setActiveTab('monitor');
      setNewBotName('');
      setNewBotRules([]);
      setCreationStep('rules');
      setEditingBotId(null);
      setSniperStrategy('diversified');

    } catch (error) {
      console.error('Error saving sniper bot:', error);
      // Could add toast notification here
    }
  };

  // Render rules section
  const renderRules = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-white text-sm font-semibold">Sniper Rules</h4>
        <button
          onClick={() => {
            setEditingRule(null);
            setShowRuleModal(true);
          }}
          className="px-3 py-1 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] text-xs hover:bg-[#24c35b]/30 transition-all"
        >
          <Plus size={12} className="mr-1 inline" />
          Add Rule
        </button>
      </div>

      {isLoadingRules ? (
        <div className="flex items-center justify-center py-4">
          <Loader size={16} className="text-[#24c35b] animate-spin mr-2" />
          <span className="text-gray-400 text-xs">Loading rules...</span>
        </div>
      ) : sniperRules.length === 0 ? (
        <div className="text-center py-6">
          <Settings size={24} className="text-gray-500 mx-auto mb-2" />
          <p className="text-gray-400 text-xs">No sniper rules configured</p>
          <p className="text-gray-500 text-xs mt-1">Create rules to automatically snipe tokens</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sniperRules.map(rule => (
            <div
              key={rule.id}
              className={`p-3 rounded-lg border ${
                rule.isActive 
                  ? 'border-[#24c35b]/40 bg-[#24c35b]/5' 
                  : 'border-gray-600/20 bg-gray-600/5'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <div className={`w-2.5 h-2.5 rounded-full mr-2 ${rule.isActive ? 'bg-[#24c35b]' : 'bg-gray-500'}`} />
                  <h5 className="text-white text-sm font-medium">{rule.name}</h5>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleRuleToggle(rule.id)}
                    className="text-gray-400 hover:text-white transition-colors"
                    title={rule.isActive ? 'Disable rule' : 'Enable rule'}
                  >
                    {rule.isActive ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                  </button>
                  <button
                    onClick={() => {
                      // Convert local rule back to service format for editing
                      const serviceRule: SniperRule = {
                        id: rule.id,
                        user_id: '',
                        name: rule.name,
                        is_active: rule.isActive,
                        created_at: rule.createdAt,
                        updated_at: rule.createdAt,
                        total_triggers: 0,
                        total_executed_trades: 0,
                        conditions: rule.conditions.map(c => ({
                          field: c.field === 'tokensBought' ? 'txAmount' : c.field,
                          operator: c.operator,
                          value: c.value
                        }))
                      };
                      setEditingRule(serviceRule);
                      setShowRuleModal(true);
                    }}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <Edit2 size={12} />
                  </button>
                  <button
                    onClick={() => handleRuleDelete(rule.id)}
                    className="text-gray-400 hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
              <div className="text-xs text-gray-400">
                <div className="mb-1">
                  {rule.conditions.length} condition{rule.conditions.length !== 1 ? 's' : ''}:
                </div>
                <div className="space-y-1">
                  {rule.conditions.slice(0, 3).map((condition, idx) => (
                    <div key={idx} className="text-gray-300 font-mono text-xs">
                      • {formatCondition(condition)}
                    </div>
                  ))}
                  {rule.conditions.length > 3 && (
                    <div className="text-gray-500 text-xs">
                      +{rule.conditions.length - 3} more condition{rule.conditions.length - 3 !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Render monitor section (updated to show sniper bots)
  const renderMonitor = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-white text-sm font-semibold">Active Sniper Bots</h4>
        <button
          onClick={() => {
            setIsCreatingBot(true);
            setCreationStep('rules');
            setNewBotName('');
            setNewBotRules([]);
          }}
          className="px-3 py-1 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] text-xs hover:bg-[#24c35b]/30 transition-all"
        >
          <Plus size={12} className="mr-1 inline" />
          Create New Bot
        </button>
      </div>

      {/* System Status */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 rounded-lg border border-[#24c35b]/20 bg-[#24c35b]/5">
          <div className="text-white text-lg font-bold">{displayTotalTokensTracked}</div>
          <div className="text-gray-400 text-xs">Tokens Tracked</div>
        </div>
        <div className={`p-3 rounded-lg border ${
          isSnipingActive 
            ? 'border-[#24c35b]/40 bg-[#24c35b]/10' 
            : 'border-gray-600/20 bg-gray-600/5'
        }`}>
          <div className={`text-lg font-bold ${
            isSnipingActive ? 'text-[#24c35b]' : 'text-gray-400'
          }`}>
            {isSnipingActive ? 'ACTIVE' : 'STOPPED'}
          </div>
          <div className="text-gray-400 text-xs">
            {isSnipingActive ? 'Checking Rules' : 'No Rule Checking'}
          </div>
        </div>
      </div>

      {/* Sniper Bots List */}
      {isLoadingRules ? (
        <div className="flex items-center justify-center py-4">
          <Loader size={16} className="text-[#24c35b] animate-spin mr-2" />
          <span className="text-gray-400 text-xs">Loading sniper bots...</span>
        </div>
      ) : sniperRules.length === 0 ? (
        <div className="text-center py-6">
          <Target size={24} className="text-gray-500 mx-auto mb-2" />
          <p className="text-gray-400 text-xs">No sniper bots configured</p>
          <p className="text-gray-500 text-xs mt-1">Create your first bot to start automated sniping</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sniperRules.map(rule => {
            // Get statistics for this rule
            const stats = ruleStatistics.get(rule.id) || {triggers: 0, success: 0, failed: 0};
            
            return (
              <div
                key={rule.id}
                className={`p-3 rounded-lg border ${
                  rule.isActive 
                    ? 'border-[#24c35b]/40 bg-[#24c35b]/5' 
                    : 'border-gray-600/20 bg-gray-600/5'
                }`}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <div className={`w-2.5 h-2.5 rounded-full mr-2 ${rule.isActive ? 'bg-[#24c35b]' : 'bg-gray-500'}`} />
                    <div>
                      <h5 className="text-white text-sm font-medium">{rule.name}</h5>
                      <div className="flex items-center gap-1 text-xs">
                        <span className="text-gray-500">
                        {rule.isActive ? 'Active & Monitoring' : 'Stopped'}
                        </span>
                        {/* Strategy Label */}
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          rule.configurationMetadata?.sniperStrategy === 'mirrored' || rule.configurationMetadata?.enableSniperMirroring
                            ? 'bg-blue-500/20 text-blue-400 border border-blue-500/40'
                            : rule.configurationMetadata?.sniperStrategy === 'individual'
                            ? 'bg-purple-500/20 text-purple-400 border border-purple-500/40'
                            : 'bg-green-500/20 text-green-400 border border-green-500/40'
                        }`}>
                          {rule.configurationMetadata?.sniperStrategy === 'mirrored' || rule.configurationMetadata?.enableSniperMirroring 
                            ? 'Mirrored' 
                            : rule.configurationMetadata?.sniperStrategy === 'individual'
                            ? 'Individual'
                            : 'Diversified'}
                        </span>
                        {/* Auto-Sell Label */}
                        {rule.configurationMetadata?.enableAutoSell && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-500/20 text-orange-400 border border-orange-500/40">
                            Auto-Sell {rule.configurationMetadata?.autoSellProfitPercent || 50}%
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleRuleToggle(rule.id)}
                      className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                        rule.isActive 
                          ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/40' 
                          : 'bg-[#24c35b]/20 text-[#24c35b] hover:bg-[#24c35b]/30 border border-[#24c35b]/40'
                      }`}
                    >
                      {rule.isActive ? 'Stop' : 'Start'}
                    </button>
                    <button
                      onClick={() => handleEditBot(rule)}
                      className="text-gray-400 hover:text-white transition-colors p-1"
                      title="Edit bot configuration"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleRuleDelete(rule.id)}
                      className="text-gray-400 hover:text-red-400 transition-colors p-1"
                      title="Delete bot"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Compact Configuration & Budget Row */}
                <div className="grid grid-cols-6 gap-1 mb-2">
                  <div className="p-1 rounded bg-gray-600/5 border border-gray-600/20 text-center">
                    <div className="text-[9px] text-gray-500">SOL/snipe</div>
                    <div className="text-white text-xs font-mono">
                      {rule.solAmountPerSnipe ? `${rule.solAmountPerSnipe}` : 'N/A'}
                    </div>
                  </div>
                  <div className="p-1 rounded bg-gray-600/5 border border-gray-600/20 text-center">
                    <div className="text-[9px] text-gray-500">Tokens</div>
                    <div className="text-white text-xs font-mono">
                      {rule.maxTokensToSnipe ? `${rule.maxTokensToSnipe}` : 'N/A'}
                    </div>
                  </div>
                  <div className="p-1 rounded bg-gray-600/5 border border-gray-600/20 text-center">
                    <div className="text-[9px] text-gray-500">Slip%</div>
                    <div className="text-white text-xs font-mono">
                      {rule.slippagePercent ? `${rule.slippagePercent}` : 'N/A'}
                    </div>
                  </div>
                  <div className="p-1 rounded bg-gray-600/5 border border-gray-600/20 text-center">
                    <div className="text-[9px] text-gray-500">Budget</div>
                    <div className="text-white text-xs font-mono">
                      {(() => {
                        // Calculate budget if not stored or if missing
                        const calculatedBudget = (rule.solAmountPerSnipe || 0) * (rule.maxTokensToSnipe || 0);
                        const storedBudget = rule.totalSolBudget;
                        const displayBudget = storedBudget || calculatedBudget;
                        return displayBudget > 0 ? displayBudget.toFixed(2) : 'N/A';
                      })()}
                    </div>
                  </div>
                  <div className="p-1 rounded bg-gray-600/5 border border-gray-600/20 text-center">
                    <div className="text-[9px] text-gray-500">Spent</div>
                    <div className="text-orange-400 text-xs font-mono">
                      {(() => {
                        const solPerSnipe = rule.solAmountPerSnipe || 0;
                        const successfulSnipes = stats.success || 0;
                        const totalSpent = successfulSnipes * solPerSnipe;
                        return totalSpent.toFixed(2);
                      })()}
                    </div>
                  </div>
                  <div className="p-1 rounded bg-gray-600/5 border border-gray-600/20 text-center">
                    <div className="text-[9px] text-gray-500">Wallets</div>
                    <div className="text-white text-xs font-mono">
                      {rule.tradingWalletAddresses?.length || 0}
                    </div>
                  </div>
                </div>

                {/* Active Wallets Section - Compact */}
                {rule.tradingWalletAddresses && rule.tradingWalletAddresses.length > 0 && (
                  <div className="mb-2">
                    <div className="flex items-center gap-1 mb-1">
                      <Wallet size={10} className="text-[#24c35b]" />
                      <div className="text-[10px] text-[#24c35b] font-medium">
                        Active Wallets ({rule.tradingWalletAddresses.length})
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      {rule.tradingWalletAddresses.slice(0, 4).map((walletAddr, idx) => {
                          const wallet = tradingWallets.find(w => w.public_key === walletAddr);
                          const balance = walletBalances.get(walletAddr) || 0;
                          return (
                          <div key={idx} className="p-1 rounded bg-gray-600/5 border border-gray-600/20">
                            <div className="flex items-center justify-between text-[10px]">
                              <span className="text-white font-mono truncate">
                                {wallet?.name || `${walletAddr.slice(0, 4)}...${walletAddr.slice(-3)}`}
                              </span>
                              <span className="text-[#24c35b] font-mono">
                                {balance.toFixed(2)}
                              </span>
                            </div>
                            </div>
                          );
                        })}
                      {rule.tradingWalletAddresses.length > 4 && (
                        <div className="p-1 rounded bg-gray-600/5 border border-gray-600/20 flex items-center justify-center">
                          <span className="text-[10px] text-gray-400">
                            +{rule.tradingWalletAddresses.length - 4} more
                          </span>
                          </div>
                        )}
                    </div>
                  </div>
                )}

                {/* Performance Stats - Larger */}
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <div className="text-center px-1 py-0.5 rounded bg-gray-600/5 border border-gray-600/20">
                    <div className="text-blue-400 text-3xl font-bold">
                      {stats.triggers}
                    </div>
                    <div className="text-[10px] text-gray-500">Triggers</div>
                  </div>
                  <div className="text-center px-1 py-0.5 rounded bg-gray-600/5 border border-gray-600/20">
                    <div className="text-[#24c35b] text-3xl font-bold">
                      {stats.success}
                    </div>
                    <div className="text-[10px] text-gray-500">Success</div>
                  </div>
                  <div className="text-center px-1 py-0.5 rounded bg-gray-600/5 border border-gray-600/20">
                    <div className="text-red-400 text-3xl font-bold">
                      {stats.failed}
                    </div>
                    <div className="text-[10px] text-gray-500">Failed</div>
                  </div>
                </div>

                {/* Rules Preview - Show ALL conditions */}
                <div className="border-t border-gray-600/30 pt-1">
                  <div className="text-[10px] text-gray-500 mb-0.5">
                    {rule.conditions.length} condition{rule.conditions.length !== 1 ? 's' : ''} (ALL must match):
                  </div>
                  <div className="space-y-0.5 max-h-20 overflow-y-auto">
                    {rule.conditions.map((condition, idx) => (
                      <div key={idx} className="text-gray-300 font-mono text-[10px] bg-gray-600/5 border border-gray-600/20 px-1.5 py-0.5 rounded">
                        <span className="text-blue-400 font-bold">{idx + 1}.</span> {formatCondition(condition)}
                      </div>
                    ))}
                  </div>
                  {rule.conditions.length > 3 && (
                    <div className="text-[9px] text-gray-500 mt-0.5 text-center">
                      ↑ Scroll to see all conditions ↑
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}


    </div>
  );

  // Memoized Transaction Card to prevent unnecessary re-renders
  const TransactionCard = React.memo<{ trade: any; index: number }>(({ trade, index }) => {
    const tokenData = trade.token_address ? tokenDataCacheRef.current.get(trade.token_address) : null;
    const tokenImage = tokenData?.token?.image;
    
    return (
      <div
        key={trade.id || index}
        className={`relative p-3 rounded-lg border overflow-hidden ${
          trade.status === 'success' 
            ? 'border-[#24c35b]/40 bg-[#24c35b]/5' 
            : trade.status === 'pending'
            ? 'border-yellow-500/40 bg-yellow-500/5'
            : 'border-red-500/40 bg-red-500/5'
        }`}
      >
        {/* Background overlay to control image opacity */}
        {tokenImage && (
          <div 
            className="absolute inset-0 opacity-10 pointer-events-none"
            style={{
              backgroundImage: `url(${tokenImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          />
        )}
        
        {/* Content overlay */}
        <div className="relative z-10">
          {/* Status label - Top right */}
          <div className="absolute top-0 right-0 flex items-center gap-1">
            {/* Auto-sell label */}
            {trade.type === 'auto_sell' && (
              <span className="px-2 py-1 rounded text-[10px] font-medium bg-orange-500/20 text-orange-400 border border-orange-500/40">
                AUTO-SELL
              </span>
            )}
            {/* Status label */}
            <span className={`px-2 py-1 rounded text-[10px] font-medium ${
              trade.status === 'success' 
                ? 'bg-[#24c35b]/20 text-[#24c35b] border border-[#24c35b]/40' 
                : trade.status === 'pending'
                ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40'
                : 'bg-red-500/20 text-red-400 border border-red-500/40'
            }`}>
              {trade.status.toUpperCase()}
            </span>
          </div>

          <div className="flex items-center mb-2">
            <div className="flex items-center flex-1">
              <div className="flex flex-col flex-1">
                <div className="flex items-center gap-2">
                  {/* Timer to the left of token name */}
                  <TransactionTimer createdAt={trade.created_at} />
                  <span className="text-white text-xs font-medium">
                    {trade.token_name} ({trade.token_symbol})
                  </span>
                  {trade.token_address && (
                    <button
                      onClick={() => copyTokenAddress(trade.token_address)}
                      className="text-gray-400 hover:text-white transition-colors"
                      title="Copy token address"
                    >
                      {copiedTokens.has(trade.token_address) ? (
                        <Check size={12} className="text-green-400" />
                      ) : (
                        <Copy size={12} />
                      )}
                    </button>
                  )}
                </div>
                
                {/* Token Social Links - Above action buttons */}
                {trade.status === 'success' && trade.token_address && (
                  <TokenInfoDisplay 
                    tokenAddress={trade.token_address} 
                    tokenSymbol={trade.token_symbol || 'Unknown'} 
                  />
                )}
                
                {/* Action Buttons for Successful Trades - Now underneath token name */}
                {trade.status === 'success' && trade.token_address && (
                  <div className="space-y-1 mt-1">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => openAdvancedChartsWidget(trade.token_address, trade.token_symbol || 'Unknown', trade.token_name)}
                        className="px-2 py-1 rounded text-[10px] bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/40 transition-all flex items-center"
                        title={`Open Advanced Charts widget for ${trade.token_symbol || 'token'}`}
                      >
                        <TrendingUp size={10} className="mr-1" />
                        Chart
                      </button>
                      <button
                        onClick={() => openTokenInfoWidget(trade.token_address, trade.token_symbol || 'Unknown', trade.token_name)}
                        className="px-2 py-1 rounded text-[10px] bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 border border-purple-500/40 transition-all flex items-center"
                        title={`Open Token Info widget for ${trade.token_symbol || 'token'}`}
                      >
                        <Info size={10} className="mr-1" />
                        Token
                      </button>
                    </div>
                    
                    {/* Triggered Metrics Display */}
                    {trade.triggered_conditions && (
                      <div className="p-1.5 rounded bg-gray-600/10 border border-gray-600/20">
                        <div className="text-[9px] text-gray-400 mb-0.5 font-medium">Triggered Conditions:</div>
                        <div className="space-y-0.5">
                          {trade.triggered_conditions.slice(0, 3).map((condition: any, idx: number) => (
                            <div key={idx} className="text-[9px] text-gray-300 font-mono">
                              • {formatCondition(condition)}
                            </div>
                          ))}
                          {trade.triggered_conditions.length > 3 && (
                            <div className="text-[9px] text-gray-500">
                              +{trade.triggered_conditions.length - 3} more condition{trade.triggered_conditions.length - 3 !== 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-gray-400">Amount:</span>
              <span className="text-white ml-1">{trade.amount_sol} SOL</span>
            </div>
            <div>
              <span className="text-gray-400">Wallet:</span>
              <span className="text-white ml-1 font-mono">
                {trade.trading_wallet_address?.slice(0, 6)}...{trade.trading_wallet_address?.slice(-4)}
              </span>
            </div>
          </div>
          
          {trade.transaction_signature && (
            <div className="mt-2 text-xs flex items-center justify-between">
              <div>
                <span className="text-gray-400">Txn:</span>
                <a
                  href={`https://solscan.io/tx/${trade.transaction_signature}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#24c35b] ml-1 font-mono break-all hover:text-[#2dd865] transition-colors cursor-pointer underline"
                  title="View on Solscan"
                >
                  {trade.transaction_signature.slice(0, 16)}...{trade.transaction_signature.slice(-8)}
                </a>
              </div>
              
              {/* Market info - Same row as transaction, right aligned */}
              {trade.token_address && tokenData && (
                <div>
                  {(() => {
                    const pools = tokenData.pools || [];
                    const primaryPool = pools.find((pool: any) => pool.liquidity?.usd > 0) || pools[0];
                    const marketName = primaryPool?.market || null;
                    
                    if (marketName && marketName !== 'Unknown') {
                      return (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-700/50 text-gray-300 capitalize">
                          {marketName.replace('-', ' ')}
                        </span>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </div>
          )}
          
          {trade.error_message && (
            <div className="mt-2 text-xs">
              <span className="text-red-400">Error:</span>
              <span className="text-red-300 ml-1">{trade.error_message}</span>
            </div>
          )}
        </div>
      </div>
    );
  });

  // Test function to validate sniper bot setup
  const testSniperBotSetup = async () => {
    console.log(`🧪 TESTING SNIPER BOT SETUP`);
    
    // 1. Check user wallet connection
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) {
      console.error(`❌ No user wallet connected`);
      return false;
    }
    console.log(`✅ User wallet connected: ${effectiveWalletAddress}`);
    
    // 2. Check active rules
    const activeRules = sniperRules.filter(rule => rule.isActive);
    if (activeRules.length === 0) {
      console.error(`❌ No active sniper rules`);
      return false;
    }
    console.log(`✅ ${activeRules.length} active rule(s) found`);
    
    // 3. Check selected wallets
    if (selectedWallets.size === 0) {
      console.error(`❌ No trading wallets selected`);
      console.error(`💡 Available wallets: ${tradingWallets.length}, Active wallets: ${tradingWallets.filter(w => w.is_active).length}`);
      if (tradingWallets.length > 0) {
        console.error(`📋 Wallet details:`, tradingWallets.map(w => ({
          name: w.name,
          address: w.public_key.slice(0, 8) + '...',
          active: w.is_active,
          balance: (walletBalances.get(w.public_key) || 0).toFixed(4) + ' SOL'
        })));
      }
      return false;
    }
    console.log(`✅ ${selectedWallets.size} trading wallet(s) selected`);
    
    // 4. Check wallet balances
    const totalNeeded = calculateTotalSolNeeded();
    const totalAvailable = getTotalAvailableBalance();
    if (totalAvailable < totalNeeded) {
      console.error(`❌ Insufficient balance: Need ${totalNeeded.toFixed(4)} SOL, have ${totalAvailable.toFixed(4)} SOL`);
      return false;
    }
    console.log(`✅ Sufficient balance: ${totalAvailable.toFixed(4)} SOL available, ${totalNeeded.toFixed(4)} SOL needed`);
    
    // 5. Test trade engine availability
    try {
      if (!tradeEngine) {
        console.error(`❌ Trade engine not available`);
        return false;
      }
      console.log(`✅ Trade engine available`);
    } catch (error) {
      console.error(`❌ Trade engine error:`, error);
      return false;
    }
    
    // 6. Check connection status
    if (connectionStatus !== 'connected') {
      console.error(`❌ WebSocket not connected (${connectionStatus})`);
      return false;
    }
    console.log(`✅ WebSocket connected`);
    
    console.log(`🎯 SNIPER BOT SETUP VALIDATION: ALL CHECKS PASSED`);
    console.log(`📊 Configuration Summary:`);
    console.log(`   - Rules: ${activeRules.length} active`);
    
    // Show actual configuration for each active rule instead of generic form values
    activeRules.forEach(rule => {
      console.log(`   📋 Bot "${rule.name}":`);
      console.log(`      - SOL per snipe: ${rule.solAmountPerSnipe || 'NOT CONFIGURED'}`);
      console.log(`      - Slippage: ${rule.slippagePercent || 'NOT CONFIGURED'}%`);
      console.log(`      - Max tokens: ${rule.maxTokensToSnipe || 'NOT CONFIGURED'}`);
      console.log(`      - Wallets: ${rule.tradingWalletAddresses?.length || 'NOT CONFIGURED'}`);
    });
    
    console.log(`   - Total budget: ${totalNeeded.toFixed(4)} SOL`);
    console.log(`   - Available: ${totalAvailable.toFixed(4)} SOL`);
    
    return true;
  };

  // Expose test function to window for manual testing
  React.useEffect(() => {
    (window as any).testSniperBot = testSniperBotSetup;
    return () => {
      delete (window as any).testSniperBot;
    };
  }, [sniperRules.length, selectedWallets.size, connectionStatus]); // Use .length and .size instead of full objects

  // Periodic status updates for debugging - FIXED to prevent re-renders
  useEffect(() => {
    if (!isSnipingActive) return;
    
    const logStatus = () => {
      const activeRules = sniperRules.filter(rule => rule.isActive);
      console.log(`📈 SNIPER STATUS: ${activeRules.length} active rule(s), tracking ${subscribedTokensRef.current.size} tokens`);
      
      if (activeRules.length > 0) {
        activeRules.forEach(rule => {
          console.log(`🤖 Bot "${rule.name}": ${rule.solAmountPerSnipe || 'UNCONFIGURED'} SOL/snipe, ${rule.slippagePercent || 'UNCONFIGURED'}% slippage, ${rule.tradingWalletAddresses?.length || 0} wallets`);
        });
      }
      
      if (subscribedTokensRef.current.size > 0) {
        console.log(`🎯 Recent tokens: ${Array.from(subscribedTokensRef.current).slice(-3).map(addr => addr.slice(0, 8)).join(', ')}`);
      } else {
        console.log(`⚠️ No tokens being tracked - check WebSocket connection`);
      }
    };
    
    // Log status immediately and then every 60 seconds (reduced frequency)
    logStatus();
    const interval = setInterval(logStatus, 60000);
    
    return () => clearInterval(interval);
  }, [isSnipingActive]); // Only depend on isSnipingActive, not token count or rules

  // Debug function to manually test rules against current tokens
  const debugRulesCheck = () => {
    console.log(`🔧 MANUAL RULES DEBUG`);
    console.log(`System active: ${isSnipingActive}`);
    console.log(`Tracked tokens: ${subscribedTokensRef.current.size}`);
    
    const activeRules = sniperRules.filter(rule => rule.isActive);
    console.log(`Active rules: ${activeRules.length}`);
    
    if (activeRules.length === 0) {
      console.log(`❌ No active rules to test`);
      return;
    }
    
    if (subscribedTokensRef.current.size === 0) {
      console.log(`❌ No tokens to test against`);
      return;
    }
    
    console.log(`📋 Active rules:`, activeRules.map(r => ({
      name: r.name,
      conditions: r.conditions.map(c => `${c.field} ${c.operator} ${c.value}`)
    })));
    
    // Test rules against first few tokens
    const tokenAddresses = Array.from(subscribedTokensRef.current).slice(0, 3);
    console.log(`🧪 Testing against ${tokenAddresses.length} tokens:`, tokenAddresses);
    
    tokenAddresses.forEach(tokenAddress => {
      const tokenData = masterTokenDataRef.current.get(tokenAddress);
      const tokenInfo = tokenMetadataRef.current.get(tokenAddress);
      
      if (!tokenData) {
        console.log(`❌ No data for token ${tokenAddress}`);
        return;
      }
      
      console.log(`\n🪙 Testing ${tokenInfo?.name} (${tokenInfo?.symbol}):`);
      console.log(`Available data:`, {
        marketCapUsd: tokenData.marketCapUsd,
        liquidityUsd: tokenData.liquidityUsd,
        holders: tokenData.holders,
        buys: tokenData.buys,
        sells: tokenData.sells,
        curvePercentage: tokenData.curvePercentage,
        tokensBought: tokenData.tokensBought,
        lastTxVolume: tokenData.lastTxVolume
      });
      
      // Manually check rules
      checkSniperRules(tokenAddress, tokenData);
    });
  };

  // Helper function to manually select all active wallets
  const selectAllActiveWallets = () => {
    const activeWallets = tradingWallets.filter(wallet => wallet.is_active);
    const walletAddresses = new Set(activeWallets.map(wallet => wallet.public_key));
    setSelectedWallets(walletAddresses);
    
    console.log(`✅ Manually selected ${activeWallets.length} active trading wallets`);
    activeWallets.forEach(wallet => {
      const balance = walletBalances.get(wallet.public_key) || 0;
      console.log(`   - ${wallet.name}: ${wallet.public_key.slice(0, 8)}... (${balance.toFixed(4)} SOL)`);
    });
    
    return walletAddresses.size;
  };

  // Expose debug functions to window
  React.useEffect(() => {
    // Debug function to check state synchronization - CRITICAL for diagnosing unauthorized trades
    const debugSniperBotState = () => {
      console.log(`🔍 SNIPER BOT STATE DIAGNOSTIC:`, {
        timestamp: new Date().toISOString(),
        
        // State values
        state_isSnipingActive: isSnipingActive,
        state_sniperRules: sniperRules.map(r => ({
          id: r.id,
          name: r.name,
          isActive: r.isActive
        })),
        
        // Ref values
        ref_isSnipingActive: isSnipingActiveRef.current,
        ref_sniperRules: sniperRulesRef.current.map(r => ({
          id: r.id,
          name: r.name,
          isActive: r.isActive
        })),
        
        // Comparison
        state_ref_sync: {
          isSnipingActive_match: isSnipingActive === isSnipingActiveRef.current,
          rules_count_match: sniperRules.length === sniperRulesRef.current.length,
          rules_active_state: sniperRules.filter(r => r.isActive).length,
          rules_active_ref: sniperRulesRef.current.filter(r => r.isActive).length
        },
        
        // Rule by rule comparison
        rule_mismatches: sniperRules.map(stateRule => {
          const refRule = sniperRulesRef.current.find(r => r.id === stateRule.id);
          return {
            id: stateRule.id,
            name: stateRule.name,
            state_active: stateRule.isActive,
            ref_active: refRule?.isActive || 'NOT_FOUND',
            synchronized: stateRule.isActive === refRule?.isActive
          };
        }).filter(r => !r.synchronized),
        
        // System safety status
        safety_status: {
          safe_to_trade: isSnipingActive && isSnipingActiveRef.current && 
                         sniperRules.some(r => r.isActive) && 
                         sniperRulesRef.current.some(r => r.isActive),
          active_rules_state: sniperRules.filter(r => r.isActive).map(r => r.name),
          active_rules_ref: sniperRulesRef.current.filter(r => r.isActive).map(r => r.name)
        }
      });
      
      // Return the diagnostic data for programmatic access
      return {
        stateActive: isSnipingActive,
        refActive: isSnipingActiveRef.current,
        stateRules: sniperRules.length,
        refRules: sniperRulesRef.current.length,
        synchronized: isSnipingActive === isSnipingActiveRef.current && 
                      sniperRules.length === sniperRulesRef.current.length
      };
    };

    (window as any).testSniperBot = testSniperBotSetup;
    (window as any).debugSniperRules = debugRulesCheck;
    (window as any).debugSniperState = debugSniperBotState; // NEW: State synchronization debugger
    (window as any).selectAllWallets = selectAllActiveWallets;
    (window as any).testRuleWithFakeData = testRuleWithFakeData;
    (window as any).inspectTokenData = inspectTokenData; // NEW: Inspect actual token data
    (window as any).refreshWalletBalances = refreshTradingWalletBalances; // NEW: Manual balance refresh
    (window as any).pollToken = pollTokenData; // NEW: Manual token polling
    (window as any).checkSniperWallet = () => {
      const effective = getEffectiveWalletAddress();
      console.log('🔍 SNIPER WALLET CHECK:', {
        effectiveAddress: effective,
        directAddress: typeof window !== 'undefined' && window.solana?.publicKey ? window.solana.publicKey.toString() : 'none',
        hookAddress: walletUser.walletAddress,
        usingDirect: effective === (typeof window !== 'undefined' && window.solana?.publicKey ? window.solana.publicKey.toString() : null),
        windowSolanaExists: typeof window !== 'undefined' ? !!window.solana : false,
        windowSolanaConnected: typeof window !== 'undefined' && window.solana ? !!window.solana.publicKey : false
      });
      return effective;
    };
    return () => {
      delete (window as any).testSniperBot;
      delete (window as any).debugSniperRules;
      delete (window as any).debugSniperState;
      delete (window as any).selectAllWallets;
      delete (window as any).testRuleWithFakeData;
      delete (window as any).inspectTokenData;
      delete (window as any).refreshWalletBalances;
      delete (window as any).pollToken;
      delete (window as any).checkSniperWallet;
    };
  }, [sniperRules.length, selectedWallets.size, connectionStatus]); // Use .length and .size instead of full objects

  // Test function to force rule checking with fake data that should match
  const testRuleWithFakeData = () => {
    console.log(`🧪 TESTING RULE WITH FAKE DATA`);
    
    const activeRules = sniperRules.filter(rule => rule.isActive);
    if (activeRules.length === 0) {
      console.log(`❌ No active rules to test`);
      return;
    }
    
    // Get the most recent token
    const tokenAddresses = Array.from(subscribedTokensRef.current);
    if (tokenAddresses.length === 0) {
      console.log(`❌ No tokens available`);
      return;
    }
    
    const testTokenAddress = tokenAddresses[tokenAddresses.length - 1];
    const tokenInfo = tokenMetadataRef.current.get(testTokenAddress);
    
    console.log(`🎯 Testing with fake data on: ${tokenInfo?.name} (${testTokenAddress.slice(0, 8)}...)`);
    
    // Create fake data that should match most rules
    const fakeData = {
      marketCapUsd: 50000,
      liquidityUsd: 25000,
      holders: 100,
      buys: 50,
      sells: 10,
      curvePercentage: 75,
      tokensBought: 1000,
      lastTxVolume: 5000,
      priceUsd: 0.001
    };
    
    console.log(`📊 Using fake data:`, fakeData);
    
    // Force rule checking with this fake data
    checkSniperRules(testTokenAddress, fakeData);
  };

  // NEW: Debug function to inspect actual token data for a specific token
  const inspectTokenData = (tokenAddress?: string) => {
    console.log(`🔍 INSPECTING TOKEN DATA`);
    
    const tokenAddresses = Array.from(subscribedTokensRef.current);
    if (tokenAddresses.length === 0) {
      console.log(`❌ No tokens being tracked`);
      return;
    }
    
    const targetToken = tokenAddress || tokenAddresses[tokenAddresses.length - 1];
    const tokenData = masterTokenDataRef.current.get(targetToken);
    const tokenInfo = tokenMetadataRef.current.get(targetToken);
    
    console.log(`🪙 Token: ${tokenInfo?.name || 'Unknown'} (${tokenInfo?.symbol || 'Unknown'})`);
    console.log(`📍 Address: ${targetToken}`);
    console.log(`📊 Available Data Fields:`);
    
    if (!tokenData) {
      console.log(`❌ No data found for this token`);
      return;
    }
    
    // Show all available fields and their values
    Object.entries(tokenData).forEach(([field, value]) => {
      console.log(`   ${field}: ${value} (${typeof value})`);
    });
    
    console.log(`\n🎯 Field Mapping Check:`);
    const fieldMappings = {
      'marketCap': 'marketCapUsd',
      'liquidity': 'liquidityUsd', 
      'holders': 'holders',
      'buys': 'buys',
      'sells': 'sells',
      'curvePercentage': 'curvePercentage',
      'tokensBought': 'tokensBought',
      'txVolume': 'lastTxVolume'
    };
    
    Object.entries(fieldMappings).forEach(([uiField, dataField]) => {
      const value = tokenData[dataField];
      const available = value !== undefined && value !== null;
      console.log(`   ${uiField} → ${dataField}: ${available ? '✅' : '❌'} ${available ? `(${value})` : '(missing)'}`);
    });
    
    return tokenData;
  };

  // Auto-switch strategy when wallet selection changes
  useEffect(() => {
    // If diversified or mirrored mode is selected but only one wallet is chosen, switch to individual
    if ((sniperStrategy === 'diversified' || sniperStrategy === 'mirrored') && selectedWallets.size <= 1) {
      setSniperStrategy('individual');
      console.log('🔄 Auto-switched to Individual mode (only one wallet selected)');
    }
  }, [selectedWallets.size, sniperStrategy]);

  // Copy token address to clipboard
  const copyTokenAddress = async (tokenAddress: string) => {
    try {
      await navigator.clipboard.writeText(tokenAddress);
      setCopiedTokens(prev => new Set([...prev, tokenAddress]));
      
      // Remove from copied set after 2 seconds
      setTimeout(() => {
        setCopiedTokens(prev => {
          const newSet = new Set(prev);
          newSet.delete(tokenAddress);
          return newSet;
        });
      }, 2000);
      
      showNotification(`Token address copied to clipboard!`, 'success');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      showNotification(`Failed to copy address`, 'error');
    }
  };

  // OPTIMIZED: Memoize the refresh handler to prevent unnecessary re-renders
  const handleRefreshTransactions = useCallback(() => {
    loadTradeHistory();
    loadRuleStatistics();
  }, []);

  // OPTIMIZED: Memoize the transaction list to prevent unnecessary re-renders
  const memoizedTransactionList = useMemo(() => {
    if (isLoadingTradeHistory) {
      return (
        <div className="flex items-center justify-center py-4">
          <Loader size={16} className="text-[#24c35b] animate-spin mr-2" />
          <span className="text-gray-400 text-xs">Loading trade history...</span>
        </div>
      );
    }

    // Filter out failed transactions - only show successful and pending trades
    const successfulTrades = tradeHistory.filter(trade => trade.status !== 'failed');
    
    // Combine regular trades with auto-sell transactions
    const allTransactions = [
      ...successfulTrades,
      ...autoSellTransactions
    ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    if (allTransactions.length === 0) {
      return (
        <div className="text-center py-6">
          <History size={24} className="text-gray-500 mx-auto mb-2" />
          <p className="text-gray-400 text-xs">No trades yet</p>
          <p className="text-gray-500 text-xs mt-1">Sniper trades and auto-sells will appear here</p>
        </div>
      );
    }

    return (
      <div className="space-y-2 flex-1 overflow-y-auto">
        {allTransactions.map((trade, index) => (
          <TransactionCard 
            key={trade.id || `${trade.token_address}-${index}`} 
            trade={trade} 
            index={index}
          />
        ))}
      </div>
    );
  }, [tradeHistory, autoSellTransactions, isLoadingTradeHistory]);

  // OPTIMIZED: Memoize the entire transactions render to prevent unnecessary re-renders
  const renderTransactions = useMemo(() => (
    <div className="flex flex-col h-full space-y-4">
      <div className="flex items-center justify-between flex-shrink-0">
        <h4 className="text-white text-sm font-semibold">Trades & Auto-Sells</h4>
        <button
          onClick={handleRefreshTransactions}
          className="px-3 py-1 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] text-xs hover:bg-[#24c35b]/30 transition-all"
        >
          <History size={12} className="mr-1 inline" />
          Refresh
        </button>
      </div>
      {memoizedTransactionList}
    </div>
  ), [handleRefreshTransactions, memoizedTransactionList]);

  // Main content for widget
  const content = (
    <div
      className={`w-full h-full flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm ${widgetClassName}`}
    >
      <style>{globalWidgetTextStyles}</style>
      <style>{`
        /* Hide number input arrows */
        input[type=number]::-webkit-outer-spin-button,
        input[type=number]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>
      {/* Header */}
      <div className="p-3 border-b border-gray-200 bg-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#24c35b]"></div>
          <Target size={18} className="text-[#24c35b]" />
          <h2 className="text-lg font-bold text-black">Sniper Bot</h2>
        </div>
        {onClose && (
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-black transition-colors p-1 rounded hover:bg-gray-100"
            title="Close"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 bg-gray-50">
        {!isCreatingBot ? (
          // Normal tabs when not creating a bot
          [
            { key: 'monitor', label: 'Monitor', icon: Activity },
            { key: 'transactions', label: 'Transactions', icon: History }
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key as any)}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-all border-b-2 ${
                activeTab === key
                  ? 'border-[#24c35b] text-[#24c35b] bg-white'
                  : 'border-transparent text-gray-600 hover:text-black hover:bg-gray-100'
              }`}
            >
              <Icon size={16} className="mr-2 inline" />
              {label}
            </button>
          ))
        ) : (
          // Creation flow tabs
          <div className="flex w-full">
            <div className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 ${
              creationStep === 'rules' 
                ? 'border-[#24c35b] text-[#24c35b] bg-white'
                : 'border-transparent text-gray-600'
            }`}>
              <Target size={16} className="mr-2 inline" />
              1. Set Rules
            </div>
            <div className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 ${
              creationStep === 'config' 
                ? 'border-[#24c35b] text-[#24c35b] bg-white'
                : 'border-transparent text-gray-600'
            }`}>
              <Settings size={16} className="mr-2 inline" />
              2. Configure
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-grow overflow-y-auto px-4 py-3" style={{ background: '#ffffff', borderRadius: 0, overflowX: 'visible' }}>
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader size={20} className="text-[#24c35b] animate-spin mr-3" />
            <span className="text-gray-700 text-sm">Initializing sniper bot...</span>
          </div>
        )}

        {/* Tab Content */}
        {!isLoading && (
          <>
            {!isCreatingBot ? (
              // Normal tab content
              <>
                {activeTab === 'monitor' && renderMonitor()}
                {activeTab === 'transactions' && renderTransactions}
              </>
            ) : (
              // Creation flow content
              <>
                {creationStep === 'rules' && renderRulesCreation()}
                {creationStep === 'config' && renderConfigCreation()}
              </>
            )}
          </>
        )}
      </div>

      {/* Sniper Rule Modal */}
      <SniperRuleModal
        isOpen={showRuleModal}
        onClose={() => {
          setShowRuleModal(false);
          setEditingRule(null);
        }}
        onSave={handleRuleSave}
        editingRule={editingRule}
      />

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && ruleToDelete && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-gray-200 rounded-lg w-full max-w-md shadow-xl">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-black text-lg font-semibold flex items-center">
                <Trash2 size={20} className="text-red-500 mr-2" />
                Delete Sniper Bot
              </h3>
            </div>

            {/* Content */}
            <div className="p-4">
              {/* Active bot warning */}
              {ruleToDelete.isActive && (
                <div className="mb-4 p-3 rounded-lg border border-yellow-500/40 bg-yellow-500/10">
                  <div className="flex items-center mb-2">
                    <AlertCircle size={16} className="text-yellow-600 mr-2" />
                    <span className="text-yellow-600 font-medium text-sm">Active Bot Warning</span>
                  </div>
                  <p className="text-yellow-700 text-xs">
                    This bot is currently active and monitoring tokens. Deleting it will immediately stop all monitoring and trading.
                  </p>
                </div>
              )}

              <p className="text-black text-sm mb-2">
                Are you sure you want to delete the sniper bot:
              </p>
              <p className="text-[#24c35b] font-semibold text-lg mb-4">
                "{ruleToDelete.name}"
              </p>
              <p className="text-gray-600 text-xs">
                This action cannot be undone and will permanently remove the bot and all its configuration.
              </p>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 flex items-center justify-end space-x-2">
              <button
                onClick={cancelRuleDelete}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:text-black hover:border-gray-400 transition-all text-sm"
              >
                Cancel
              </button>
              <button
                onClick={confirmRuleDelete}
                className="px-4 py-2 rounded-lg bg-red-50 border border-red-300 text-red-600 hover:bg-red-100 transition-all text-sm flex items-center"
              >
                <Trash2 size={14} className="mr-1" />
                Delete Bot
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // If this is being rendered as a widget, return just the content
  if (isWidget) {
    return (
      <div className="absolute inset-0 m-0 p-0 overflow-hidden">{content}</div>
    );
  }

  // Otherwise, return the modal version with container
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className={`bg-white border border-gray-200/50 rounded-lg w-full max-w-[420px] shadow-xl ${widgetClassName}`}>
        {content}
      </div>
    </div>
  );
};

// Factory function to create widget
export function createSniperBotWidget(onAddWidget?: (widget: any) => void) {
  return {
    type: "sniper-bot",
    title: "Sniper Bot",
    content: <SniperBot isWidget={true} onAddWidget={onAddWidget} />,
    size: { width: 600, height: 600 },
    contentStyle: { 
      padding: 0,
      overflow: "hidden",
    },
    glassMorphism: true,
    containerStyle: {
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      backdropFilter: "blur(12px)",
      borderRadius: "12px",
      border: "1px solid rgba(36, 195, 91, 0.2)",
      boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(36, 195, 91, 0.2)",
      overflow: "hidden",
    },
  };
}

export default SniperBot; 